/**
 * BaseApplication - Universal Application Framework
 * 
 * Core framework that all v2 apps extend (Docs, Canvas, Sheets, Presentations, Notes)
 * Provides common functionality: indexing, AI commands, progressive loading, etc.
 */

import * as Y from 'yjs';
import { Node as ProseMirrorNode } from 'prosemirror-model';
import { EditorView } from 'prosemirror-view';
import { EditorState } from 'prosemirror-state';
import { debounce } from 'lodash';
import { WEBSOCKET_URL } from '../../api/config';

// Indices
import { IndexManager } from './IndexManager'; // ✅ NEW: Optimized index system
import { SpatialIndex } from './indices/SpatialIndex';
import { GridIndex } from './indices/GridIndex';
import { SlideIndex } from './indices/SlideIndex';

// Types
import { AppConfig, LoadingPhase } from './types';

// Services
import { entityService } from '../entityService';

// Find & Replace types
export interface FindResult {
  nodeId: string;
  text: string;
  matchCount: number;
  className?: string;
  position?: { row?: number; col?: number; index?: number };
}

export interface ReplaceResult {
  affectedNodes: string[];
  replacementCount: number;
}

export abstract class BaseApplication {
  // Y.js document
  protected ydoc?: Y.Doc;
  protected yxml?: Y.XmlFragment;
  
  // ProseMirror
  protected editorView?: EditorView;
  protected editorState?: EditorState;
  
  // Entity reference
  protected entityId?: string;
  
  // ✅ NEW: Optimized index manager
  protected indexManager: IndexManager;
  
  // Indices (app-specific)
  protected spatialIndex?: SpatialIndex;
  protected gridIndex?: GridIndex;
  protected slideIndex?: SlideIndex;
  
  // ⚠️ DEPRECATED: Old indices (kept for backward compatibility)
  // TODO: Remove these after verifying IndexManager works
  protected byIdIndex: Map<string, ProseMirrorNode> = new Map();
  protected byClassIndex: Map<string, Set<string>> = new Map();
  protected fullTextIndex: Map<string, Set<string>> = new Map();
  
  // Loading state
  protected loadingPhase: LoadingPhase = 'not-started';
  protected loadingProgress = {
    minimal: false,
    partial: false,
    full: false
  };
  
  // Event emitter
  protected eventHandlers: Map<string, Set<Function>> = new Map();
  
  // ✅ CHANGE TRACKING: Enable AI suggestion review workflow
  // When enabled, AI edits create tracked changes requiring user accept/reject
  protected suggestionMode: boolean = true;  // Enable by default
  protected currentRevisionId: string | null = null;
  
  // ✅ NEW: Unified Persistence Layer
  protected autoSaveEnabled: boolean = true;
  protected saveDebounceMs: number = 500;
  protected saveMaxWaitMs: number = 5000;
  private debouncedSave?: ReturnType<typeof debounce>;
  private ydocObserver?: (update: Uint8Array, origin: any) => void;
  
  // ✅ FIX (Bug #2): Concurrency protection for saves
  private savePending: boolean = false;
  private saveQueued: boolean = false;
  
  // ✅ FIX #3: Track state for validation
  private lastSaveTimestamp: number = 0;
  private lastSaveStateSize: number = 0;
  
  constructor() {
    console.log('[BaseApplication] Constructor called');
    
    // ✅ NEW: Initialize IndexManager
    this.indexManager = new IndexManager();
  }
  
  /**
   * Get app configuration (implemented by subclass)
   */
  protected abstract getConfig(): AppConfig;
  
  /**
   * Initialize application
   * Loads entity and sets up indices
   */
  async initialize(entityId: string): Promise<void> {
    console.log('[BaseApplication] Initializing for entity:', entityId);
    
    this.entityId = entityId;
    this.loadingPhase = 'loading-minimal';
    
    try {
      // 1. Initialize Y.js document
      await this.initializeYDoc();
      
      // 3. Build core indices (byId, byClassName)
      await this.buildCoreIndices();
      
      // 4. Mark minimal ready
      this.loadingProgress.minimal = true;
      this.loadingPhase = 'ready-minimal';
      this.emit('ready');
      
      console.log('[BaseApplication] ✅ Minimal load complete');
      
      // 5. Load partial data in background
      this.loadPartialDataInBackground();
      
    } catch (error) {
      console.error('[BaseApplication] Initialization failed:', error);
      this.loadingPhase = 'error';
      throw error;
    }
  }
  
  
  /**
   * Initialize Y.js document
   */
  protected async initializeYDoc(): Promise<void> {
    console.log('[BaseApplication] Initializing Y.js document');
    
    this.ydoc = new Y.Doc();
    
    // Get or create containers
    const containers = this.ydoc.getMap('containers');
    
    // Get first container (or create default)
    const config = this.getConfig();
    const containerId = `container_1`;
    
    if (!containers.has(containerId)) {
      // Create default container
      const container = new Y.Map();
      container.set('id', containerId);
      container.set('type', config.containerType);
      
      const content = new Y.XmlFragment();
      container.set('content', content);
      
      containers.set(containerId, container);
    }
    
    // Get content fragment
    const container = containers.get(containerId) as Y.Map<any>;
    this.yxml = container.get('content') as Y.XmlFragment;
    
    console.log('[BaseApplication] ✅ Y.js document initialized');
  }
  
  /**
   * Build core indices from Y.js document
   * ✅ UPDATED: Now uses optimized IndexManager
   */
  protected async buildCoreIndices(): Promise<void> {
    console.log('[BaseApplication] Building core indices with IndexManager');
    
    if (!this.ydoc) {
      console.warn('[BaseApplication] No ydoc, skipping indices');
      return;
    }
    
    // 🔍 DEBUG: Check yxml state before indexing
    const yxml = this.yxml ?? this.ydoc.getXmlFragment('prosemirror');
    console.log('[BaseApplication] 🔍 yxml state before indexing:', {
      length: yxml.length,
      firstChild: (yxml.get(0) as any)?.nodeName || 'none',
      ydocGuid: this.ydoc.guid
    });
    
    // 🔧 AUTO-FIX: If yxml is empty, initialize with app-specific content
    if (yxml.length === 0) {
      if ((this as any).initializeEmptySpreadsheet) {
        console.log('[BaseApplication] 🚨 EMPTY YXML DETECTED (Sheets) - Running auto-fix...');
        (this as any).initializeEmptySpreadsheet();
      } else if ((this as any).initializeEmptyPresentation) {
        console.log('[BaseApplication] 🚨 EMPTY YXML DETECTED (Presentations) - Running auto-fix...');
        (this as any).initializeEmptyPresentation();
      }
      
      // Re-check yxml after init
      const yxmlAfter = this.ydoc.getXmlFragment('prosemirror');
      console.log('[BaseApplication] ✅ Auto-fix complete, yxml now has', yxmlAfter.length, 'elements');
      console.log('[BaseApplication] 🔍 After auto-fix:', {
        length: yxmlAfter.length,
        firstChild: (yxmlAfter.get(0) as any)?.nodeName || 'none'
      });
    }
    
    // ✅ NEW: Use IndexManager (optimized!)
    const stats = await this.indexManager.buildIndices(this.ydoc);
    
    // ⚠️ BACKWARD COMPATIBILITY: Sync to old indices
    // TODO: Remove this after confirming IndexManager works
    this.syncIndexManagerToOldIndices();
    
    console.log('[BaseApplication] ✅ Core indices built:', stats);
  }
  
  /**
   * Sync IndexManager results to old indices (backward compatibility)
   * ⚠️ DEPRECATED: Remove after IndexManager is proven
   */
  private syncIndexManagerToOldIndices(): void {
    // Clear old indices
    this.byIdIndex.clear();
    this.byClassIndex.clear();
    
    // Sync byId
    for (const [id, ref] of this.indexManager.getAllEntries()) {
      this.byIdIndex.set(id, ref.element as any);
    }
    
    // Sync byClassName
    for (const [id, ref] of this.indexManager.getAllEntries()) {
      const element = ref.element;
      const className = element.getAttribute('className');
      
      if (className) {
        if (!this.byClassIndex.has(className)) {
          this.byClassIndex.set(className, new Set());
        }
        this.byClassIndex.get(className)!.add(id);
      }
    }
    
    console.log('[BaseApplication] 🔄 Synced IndexManager to old indices:', {
      byIdCount: this.byIdIndex.size,
      byClassCount: this.byClassIndex.size,
      classNames: Array.from(this.byClassIndex.keys())
    });
  }
  
  /**
   * Index Y.XmlFragment recursively
   */
  protected indexYXmlFragment(fragment: Y.XmlFragment): void {
    for (let i = 0; i < fragment.length; i++) {
      const child = fragment.get(i);
      
      if (child instanceof Y.XmlElement) {
        this.indexYXmlElement(child);
      }
    }
  }
  
  /**
   * Index Y.XmlElement
   */
  protected indexYXmlElement(element: Y.XmlElement): void {
    // Get attributes
    const id = element.getAttribute('id');
    const className = element.getAttribute('className');
    
    // Index by ID
    if (id) {
      // For now, store Y.XmlElement directly
      // In production, convert to ProseMirror node
      this.byIdIndex.set(id, element as any);
    }
    
    // Index by className
    if (className) {
      if (!this.byClassIndex.has(className)) {
        this.byClassIndex.set(className, new Set());
      }
      if (id) {
        this.byClassIndex.get(className)!.add(id);
      }
    }
    
    // Index text content for full-text search
    const text = this.extractTextFromElement(element);
    if (text && id) {
      const words = text.toLowerCase().split(/\s+/);
      for (const word of words) {
        if (word.length > 2) { // Skip very short words
          if (!this.fullTextIndex.has(word)) {
            this.fullTextIndex.set(word, new Set());
          }
          this.fullTextIndex.get(word)!.add(id);
        }
      }
    }
    
    // Recursively index children
    for (let i = 0; i < element.length; i++) {
      const child = element.get(i);
      if (child instanceof Y.XmlElement) {
        this.indexYXmlElement(child);
      }
    }
  }
  
  /**
   * Extract text from Y.XmlElement
   */
  protected extractTextFromElement(element: Y.XmlElement): string {
    let text = '';
    
    for (let i = 0; i < element.length; i++) {
      const child = element.get(i);
      
      if (child instanceof Y.XmlText) {
        text += child.toString();
      } else if (child instanceof Y.XmlElement) {
        text += this.extractTextFromElement(child);
      }
    }
    
    return text;
  }
  
  /**
   * Load partial data in background
   */
  protected async loadPartialDataInBackground(): Promise<void> {
    console.log('[BaseApplication] Loading partial data in background...');
    
    setTimeout(async () => {
      try {
        // Build app-specific indices (spatial, grid, etc.)
        await this.buildAppSpecificIndices();
        
        // Build full-text index (if not done)
        // Already done in buildCoreIndices
        
        this.loadingProgress.partial = true;
        this.loadingPhase = 'ready-partial';
        this.emit('partial-ready');
        
        console.log('[BaseApplication] ✅ Partial load complete');
        
        // Start full load
        this.loadFullDataInBackground();
        
      } catch (error) {
        console.error('[BaseApplication] Partial load failed:', error);
      }
    }, 0);
  }
  
  /**
   * Build app-specific indices (spatial, grid, slide)
   * ✅ UPDATED: Now actually populates the indices
   */
  protected async buildAppSpecificIndices(): Promise<void> {
    const config = this.getConfig();
    
    // Initialize and populate spatial index (Canvas, Presentations)
    if (config.layoutMode === 'spatial' || config.layoutMode === 'slides') {
      console.log('[BaseApplication] Building spatial index');
      this.spatialIndex = new SpatialIndex();
      
      // ✅ Populate from positioned nodes
      for (const [id, ref] of this.indexManager.getAllEntries()) {
        const positionAttr = ref.element.getAttribute('position');
        
        if (positionAttr) {
          // ✅ Parse attribute (Y.js stores as JSON string)
          const position = typeof positionAttr === 'string' 
            ? JSON.parse(positionAttr) 
            : positionAttr;
          
          if (position && position.type === 'absolute') {
            this.spatialIndex.insert({
              id,
              x: position.x || 0,
              y: position.y || 0,
              width: position.width || 100,
              height: position.height || 100,
              zIndex: position.zIndex
            });
          }
        }
      }
      
      console.log('[BaseApplication] ✅ Spatial index built:', this.spatialIndex.size(), 'positioned nodes');
    }
    
    // Initialize and populate grid index (Sheets)
    if (config.layoutMode === 'grid') {
      console.log('[BaseApplication] Building grid index');
      this.gridIndex = new GridIndex();
      
      // ✅ Populate from cell nodes
      for (const [id, ref] of this.indexManager.getAllEntries()) {
        const semanticAttr = ref.element.getAttribute('semantic');
        
        if (semanticAttr) {
          // ✅ Parse attribute (Y.js stores as JSON string)
          const semantic = typeof semanticAttr === 'string' 
            ? JSON.parse(semanticAttr) 
            : semanticAttr;
          
          if (semantic?.cellRef) {
            this.gridIndex.setCell(semantic.cellRef, {
              ref: semantic.cellRef,
              value: semantic.value,
              formula: semantic.formula,
              type: semantic.cellType || 'text',
              format: semantic.format
            });
            
            // Track formula dependencies
            if (semantic.formula && semantic.dependencies) {
              semantic.dependencies.forEach((dep: string) => {
                this.gridIndex!.addDependency(semantic.cellRef, dep);
              });
            }
          }
        }
      }
      
      const stats = this.gridIndex.getStats();
      console.log('[BaseApplication] ✅ Grid index built:', stats);
    }
    
    // Initialize slide index (Presentations)
    if (config.containerType === 'slide') {
      console.log('[BaseApplication] Building slide index');
      this.slideIndex = new SlideIndex();
      
      // ✅ SlideIndex builds from containers automatically
      // (It has its own population logic)
      
      console.log('[BaseApplication] ✅ Slide index built');
    }
  }
  
  /**
   * Load full data in background
   */
  protected async loadFullDataInBackground(): Promise<void> {
    console.log('[BaseApplication] Loading full data in background...');
    
    setTimeout(async () => {
      try {
        // Load any additional data
        // For now, just mark as complete
        
        this.loadingProgress.full = true;
        this.loadingPhase = 'ready-full';
        this.emit('fully-ready');
        
        console.log('[BaseApplication] ✅ Full load complete');
        
      } catch (error) {
        console.error('[BaseApplication] Full load failed:', error);
      }
    }, 0);
  }
  
  /**
   * Handle entity_read command
   * ✅ UPDATED: Now uses optimized IndexManager for O(1) and O(log n) queries
   */
  async handleEntityRead(selector: any, options: any = {}): Promise<any> {
    console.log('[BaseApplication] ================================================');
    console.log('[BaseApplication] 🔍 handleEntityRead called');
    console.log('[BaseApplication] Selector:', JSON.stringify(selector, null, 2));
    console.log('[BaseApplication] Options:', JSON.stringify(options, null, 2));
    
    // Rebuild indices to ensure fresh data (auto-fix runs here if needed)
    console.log('[BaseApplication] 🔄 Rebuilding indices...');
    await this.buildCoreIndices();
    
    const startTime = performance.now();
    let nodeIds: Set<string> = new Set();
    
    console.log('[BaseApplication] 📊 Index stats before query:', this.indexManager.getStats());
    
    // ✅ OPTIMIZED: Use IndexManager for all queries
    if (selector.nodeId) {
      // Read single node by ID - O(1)
      console.log('[BaseApplication] 🔍 Query type: BY SINGLE NODE ID:', selector.nodeId);
      if (this.indexManager.has(selector.nodeId)) {
        nodeIds = new Set([selector.nodeId]);
        console.log('[BaseApplication] ✅ Found node by ID:', selector.nodeId);
      } else {
        nodeIds = new Set();
        console.warn('[BaseApplication] ⚠️ Node not found:', selector.nodeId);
      }
    } else if (selector.nodeIds) {
      // Read by node IDs - O(1) per ID
      console.log('[BaseApplication] 🔍 Query type: BY NODE IDS');
      nodeIds = new Set(selector.nodeIds.filter((id: string) => this.indexManager.has(id)));
      console.log('[BaseApplication] ✅ Found nodes by ID:', nodeIds.size);
    } else if (selector.className) {
      // Read by className - O(1) lookup
      console.log('[BaseApplication] 🔍 Query type: BY CLASS NAME:', selector.className);
      nodeIds = this.indexManager.getByClassName(selector.className);
      console.log('[BaseApplication] ✅ Found nodes by className:', nodeIds.size);
    } else if (selector.query) {
      // Full-text search - O(k) where k = words in query
      console.log('[BaseApplication] 🔍 Query type: FULL TEXT SEARCH:', selector.query);
      nodeIds = this.indexManager.search(selector.query);
      console.log('[BaseApplication] ✅ Found nodes by search:', nodeIds.size);
    } else if (selector.tags) {
      // Query by tags - O(1) per tag
      console.log('[BaseApplication] 🔍 Query type: BY TAGS:', selector.tags);
      if (Array.isArray(selector.tags)) {
        selector.tags.forEach((tag: string) => {
          const tagNodes = this.indexManager.getByTag(tag);
          tagNodes.forEach(id => nodeIds.add(id));
        });
      }
      console.log('[BaseApplication] ✅ Found nodes by tags:', nodeIds.size);
    } else if (selector.bounds) {
      // Spatial query by bounds - O(n) current, O(log n) with R-tree
      console.log('[BaseApplication] 🔍 Query type: SPATIAL BOUNDS:', selector.bounds);
      
      if (this.spatialIndex) {
        // Use spatial index if available (Canvas, Presentations)
        const spatialNodes = this.spatialIndex.query({
          type: 'bounds',
          bounds: selector.bounds
        });
        nodeIds = new Set(spatialNodes.map(node => node.id));
        console.log('[BaseApplication] ✅ Found nodes by bounds (spatial index):', nodeIds.size);
      } else {
        // Fallback: Manual bounds checking (slower but works)
        console.log('[BaseApplication] ⚠️ No spatial index, using fallback bounds check');
        nodeIds = new Set();
        
        for (const [id, ref] of this.indexManager.getAllEntries()) {
          const element = ref.element;
          const x = parseFloat(element.getAttribute('x') || '0');
          const y = parseFloat(element.getAttribute('y') || '0');
          const width = parseFloat(element.getAttribute('width') || '0');
          const height = parseFloat(element.getAttribute('height') || '0');
          
          // Check if element intersects with bounds
          const intersects = (
            x < selector.bounds.x + selector.bounds.width &&
            x + width > selector.bounds.x &&
            y < selector.bounds.y + selector.bounds.height &&
            y + height > selector.bounds.y
          );
          
          if (intersects) {
            nodeIds.add(id);
          }
        }
        console.log('[BaseApplication] ✅ Found nodes by bounds (fallback):', nodeIds.size);
      }
    } else if (selector.all) {
      // Read all nodes
      console.log('[BaseApplication] 🔍 Query type: ALL NODES');
      nodeIds = this.indexManager.getAllIds();
      console.log('[BaseApplication] ✅ getAllIds() returned:', nodeIds.size, 'nodes');
      console.log('[BaseApplication] 📋 Node IDs:', Array.from(nodeIds).slice(0, 10));
      
      if (nodeIds.size === 0) {
        console.error('[BaseApplication] ❌ CRITICAL: getAllIds() returned 0 nodes!');
        console.error('[BaseApplication] This means the index is EMPTY even though content exists!');
        console.error('[BaseApplication] Likely cause: Elements have NO id attributes');
      }
    } else {
      console.warn('[BaseApplication] ⚠️ Unknown selector type:', selector);
      console.warn('[BaseApplication] 📋 Valid selectors: nodeId, nodeIds, className, query, tags, bounds, containerId, all');
      console.warn('[BaseApplication] 📋 Received keys:', Object.keys(selector));
      
      // Return empty result with warning in stats
      nodeIds = new Set();
    }
    
    // Apply container filter if specified
    if (selector.containerId && nodeIds.size > 0) {
      console.log('[BaseApplication] 🔍 Applying container filter:', selector.containerId);
      const beforeFilter = nodeIds.size;
      nodeIds = new Set(
        [...nodeIds].filter(id => 
          this.indexManager.getContainer(id) === selector.containerId
        )
      );
      console.log('[BaseApplication] ✅ Container filter applied:', {
        before: beforeFilter,
        after: nodeIds.size,
        removed: beforeFilter - nodeIds.size
      });
    }
    
    // Apply range limits if specified
    if (selector.range && nodeIds.size > 0) {
      console.log('[BaseApplication] 🔍 Applying range limits:', selector.range);
      const nodeArray = [...nodeIds];
      const start = selector.range.start || 0;
      const limit = selector.range.limit || nodeArray.length;
      nodeIds = new Set(nodeArray.slice(start, start + limit));
      console.log('[BaseApplication] ✅ Range applied:', {
        start,
        limit,
        resultSize: nodeIds.size
      });
    }
    
    console.log('[BaseApplication] 📦 Converting', nodeIds.size, 'node IDs to serialized nodes...');
    
    // Convert node IDs to serialized nodes
    const nodes: any[] = [];
    for (const nodeId of nodeIds) {
      const ref = this.indexManager.getById(nodeId);
      if (ref) {
        const serialized = this.serializeNode(ref.element);
        console.log('[BaseApplication] 📄 Serialized node:', {
          id: nodeId,
          className: serialized.className,
          textLength: serialized.text?.length || 0,
          textPreview: serialized.text?.substring(0, 50) || 'N/A'
        });
        nodes.push(serialized);
      } else {
        console.warn('[BaseApplication] ⚠️ Could not get reference for nodeId:', nodeId);
      }
    }
    
    const queryTime = performance.now() - startTime;
    
    console.log('[BaseApplication] ================================================');
    console.log('[BaseApplication] ✅ Query complete:', {
      selector: Object.keys(selector),
      results: nodes.length,
      queryTime: `${queryTime.toFixed(2)}ms`
    });
    console.log('[BaseApplication] 📊 Returning:', {
      nodes: nodes.length,
      wordCount: this.calculateWordCount(nodes),
      stats: {
        nodeCount: nodes.length,
        queryTime
      }
    });
    console.log('[BaseApplication] ================================================');
    
    return {
      nodes,
      wordCount: this.calculateWordCount(nodes),
      stats: {
        nodeCount: nodes.length,
        queryTime
      }
    };
  }
  
  /**
   * Handle entity_write command
   */
  async handleEntityWrite(params: any): Promise<any> {
    console.log('[BaseApplication] handleEntityWrite:', params);
    
    if (!this.yxml) {
      throw new Error('Y.js not initialized - call initialize() first');
    }
    
    // ✅ CRITICAL FIX: Enforce batch size limit
    if (params.operation === 'batch' && params.operations) {
      if (params.operations.length > 100) {
        throw new Error(`Batch limited to 100 operations (got ${params.operations.length})`);
      }
    }
    
    // ✅ CRITICAL FIX: Rebuild indices BEFORE executing operations!
    // Operations use findNodesByTarget() which relies on byIdIndex.
    // Without fresh indices, delete/replace operations fail to find target nodes!
    await this.buildCoreIndices();
    console.log('[BaseApplication] ✅ Indices refreshed before write operation');
    
    const affectedNodeIds: string[] = [];
    
    // ✅ CRITICAL FIX: Capture ydoc and clientID before transaction to prevent null reference
    const ydoc = this.ydoc;
    if (!ydoc) {
      throw new Error('[BaseApplication] Y.js not initialized - cannot execute write');
    }
    const clientID = ydoc.clientID;
    
    // Execute in Y.js transaction (atomic)
    ydoc.transact(() => {
      const operation = params.operation || 'insert';
      
      switch (operation) {
        case 'insert':
          affectedNodeIds.push(...this.executeInsert(params));
          break;
        
        case 'replace':
          affectedNodeIds.push(...this.executeReplace(params));
          break;
        
        case 'delete':
        case 'remove':
          affectedNodeIds.push(...this.executeDelete(params));
          break;
        
        case 'update':
          affectedNodeIds.push(...this.executeUpdate(params));
          break;
        
        case 'batch':
          for (const op of params.operations || []) {
            affectedNodeIds.push(...this.executeSingleOperation(op));
          }
          break;
        
        default:
          throw new Error(`Unknown operation: ${operation}`);
      }
    });
    
    // Rebuild indices after changes
    await this.buildCoreIndices();
    
    console.log('[BaseApplication] ✅ Write complete:', {
      operation: params.operation,
      affectedNodes: affectedNodeIds.length
    });
    
    return {
      affectedNodes: affectedNodeIds,
      stats: {
        nodesModified: affectedNodeIds.length
      }
    };
  }
  
  /**
   * Handle entity_search command
   * NOTE - entity_search command is currently disabled but method kept for tests
   */
  async handleEntitySearch(query: any, options: any = {}): Promise<any> {
    console.log('[BaseApplication] handleEntitySearch:', query);
    
    // Use handleEntityRead for now
    const selector = query.text ? { query: query.text } : query;
    const result = await this.handleEntityRead(selector, options);
    
    return {
      results: result.nodes,
      totalMatches: result.nodes.length,
      executionTime: 0
    };
  }
  
  // ========================================================================
  // Universal Find & Replace
  // ========================================================================
  
  /**
   * Universal Find: Search within entity content
   * 
   * This method can be overridden by specific apps (Sheets, Docs, Canvas)
   * to provide app-specific search behavior.
   * 
   * @param query - The search term
   * @param options - Search options (matchCase, matchWholeWord, searchIn)
   * @returns Array of FindResult objects with node IDs, text, and positions
   */
  async handleFind(query: string, options?: {
    matchCase?: boolean;
    matchWholeWord?: boolean;
    searchIn?: 'content' | 'ids' | 'all';
  }): Promise<FindResult[]> {
    console.log('[BaseApplication] 🔍 Universal Find:', query, options);
    
    if (!query || query.trim() === '') {
      return [];
    }
    
    const results: FindResult[] = [];
    const searchLower = options?.matchCase ? query : query.toLowerCase();
    
    // Use IndexManager for efficient search
    if (options?.searchIn !== 'ids') {
      // Search in text content using full-text index
      const nodeIds = this.indexManager.search(query);
      
      console.log('[BaseApplication] 🔍 Found candidate nodes:', nodeIds.size);
      
      // Verify matches in full context
      for (const nodeId of nodeIds) {
        const ref = this.indexManager.getById(nodeId);
        if (!ref) continue;
        
        const textContent = this.extractTextFromElement(ref.element);
        if (!textContent) continue;
        
        const searchText = options?.matchCase ? textContent : textContent.toLowerCase();
        const searchTerm = options?.matchCase ? query : searchLower;
        
        // Check if text contains search term
        if (searchText.includes(searchTerm)) {
          // Count occurrences
          const matchCount = this.countMatches(textContent, query, options);
          
          if (matchCount > 0) {
            results.push({
              nodeId,
              text: textContent,
              matchCount,
              className: ref.element.getAttribute('className') || undefined,
            });
          }
        }
      }
    }
    
    // Search in IDs if requested
    if (options?.searchIn === 'ids' || options?.searchIn === 'all') {
      const allIds = this.indexManager.getAllIds();
      for (const nodeId of allIds) {
        const searchId = options?.matchCase ? nodeId : nodeId.toLowerCase();
        if (searchId.includes(searchLower)) {
          const ref = this.indexManager.getById(nodeId);
          if (ref) {
            results.push({
              nodeId,
              text: this.extractTextFromElement(ref.element),
              matchCount: 1,
              className: ref.element.getAttribute('className') || undefined,
            });
          }
        }
      }
    }
    
    console.log('[BaseApplication] ✅ Find complete:', results.length, 'results');
    return results;
  }
  
  /**
   * Universal Replace: Replace text within entity content
   * 
   * This method can be overridden by specific apps (Sheets, Docs, Canvas)
   * to provide app-specific replace behavior.
   * 
   * @param params - Replace parameters (find, replace, nodeIds, options)
   * @returns ReplaceResult with affected nodes and replacement count
   */
  async handleReplace(params: {
    find: string;
    replace: string;
    nodeIds?: string[];  // Specific nodes, or all if undefined
    matchCase?: boolean;
    replaceAll?: boolean;
  }): Promise<ReplaceResult> {
    console.log('[BaseApplication] 🔄 Universal Replace:', params);
    
    if (!this.yxml) {
      throw new Error('Y.js not initialized - call initialize() first');
    }
    
    const { find, replace, nodeIds, matchCase, replaceAll } = params;
    const affectedNodes: string[] = [];
    
    // Get nodes to search
    let targetNodeIds: Set<string>;
    if (nodeIds && nodeIds.length > 0) {
      targetNodeIds = new Set(nodeIds);
    } else {
      // Search all nodes that contain the find term
      const findResults = await this.handleFind(find, { matchCase });
      targetNodeIds = new Set(findResults.map(r => r.nodeId));
    }
    
    console.log('[BaseApplication] 🎯 Target nodes for replacement:', targetNodeIds.size);
    
    // Execute replacements in Y.js transaction
    this.ydoc!.transact(() => {
      for (const nodeId of targetNodeIds) {
        const ref = this.indexManager.getById(nodeId);
        if (!ref) continue;
        
        const element = ref.element;
        const textContent = this.extractTextFromElement(element);
        
        if (!textContent) continue;
        
        // Check if contains search term
        const searchText = matchCase ? textContent : textContent.toLowerCase();
        const searchTerm = matchCase ? find : find.toLowerCase();
        
        if (!searchText.includes(searchTerm)) continue;
        
        // Build regex for replacement
        const flags = matchCase ? (replaceAll ? 'g' : '') : (replaceAll ? 'gi' : 'i');
        const escapedFind = find.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(escapedFind, flags);
        
        const newText = textContent.replace(regex, replace);
        
        // Update text content
        if (newText !== textContent) {
          this.replaceElementText(element, newText);
          affectedNodes.push(nodeId);
        }
        
        if (!replaceAll) break;  // Only replace first if not replaceAll
      }
    });
    
    // Rebuild indices after replacement
    await this.buildCoreIndices();
    
    console.log('[BaseApplication] ✅ Replace complete:', affectedNodes.length, 'nodes affected');
    
    return {
      affectedNodes,
      replacementCount: affectedNodes.length,
    };
  }
  
  /**
   * Count matches of a search term in text
   */
  protected countMatches(text: string, query: string, options?: { matchCase?: boolean }): number {
    const searchText = options?.matchCase ? text : text.toLowerCase();
    const searchTerm = options?.matchCase ? query : query.toLowerCase();
    
    let count = 0;
    let pos = 0;
    
    while ((pos = searchText.indexOf(searchTerm, pos)) !== -1) {
      count++;
      pos += searchTerm.length;
    }
    
    return count;
  }
  
  /**
   * Replace all text content in a Y.XmlElement
   */
  protected replaceElementText(element: Y.XmlElement, newText: string): void {
    // Remove all existing children
    while (element.length > 0) {
      element.delete(0, 1);
    }
    
    // Insert new text with markdown parsing
    if (newText) {
      const formattedText = this.parseMarkdownToYText(newText);
      element.insert(0, [formattedText]);
    }
  }
  
  /**
   * Serialize node for AI with full metadata
   * Parses JSON attributes (position, style, semantic) for Canvas/Presentations
   */
  protected serializeNode(node: any): any {
    if (node instanceof Y.XmlElement) {
      const attrs = node.getAttributes();
      
      // Extract base fields
      const serialized: any = {
        id: attrs.id,
        className: attrs.className,
        text: this.extractTextFromElement(node)
      };
      
      // Build attrs object from JSON attributes
      const attrsObj: Record<string, any> = {};
      
      // Parse position (Canvas/Presentations spatial data)
      if (attrs.position) {
        try {
          attrsObj.position = typeof attrs.position === 'string' 
            ? JSON.parse(attrs.position) 
            : attrs.position;
        } catch (e) {
          console.warn('[BaseApplication] Failed to parse position:', e);
          attrsObj.position = attrs.position; // Keep as-is if parse fails
        }
      }
      
      // Parse style (colors, fonts, etc.)
      if (attrs.style) {
        try {
          attrsObj.style = typeof attrs.style === 'string' 
            ? JSON.parse(attrs.style) 
            : attrs.style;
        } catch (e) {
          console.warn('[BaseApplication] Failed to parse style:', e);
          attrsObj.style = attrs.style;
        }
      }
      
      // Parse semantic (element-specific metadata)
      if (attrs.semantic) {
        try {
          attrsObj.semantic = typeof attrs.semantic === 'string' 
            ? JSON.parse(attrs.semantic) 
            : attrs.semantic;
        } catch (e) {
          console.warn('[BaseApplication] Failed to parse semantic:', e);
          attrsObj.semantic = attrs.semantic;
        }
      }
      
      // ✅ CRITICAL: Convert arrow/line semantic coords from RELATIVE to ABSOLUTE
      // Internal storage: semantic coords are RELATIVE to position.x, position.y
      // AI interface: semantic coords should be ABSOLUTE canvas coordinates
      const isLineOrArrow = serialized.className === 'shape-line' || 
                            serialized.className === 'shape-arrow';
      
      if (isLineOrArrow && attrsObj.semantic && attrsObj.position) {
        try {
          // ✅ VALIDATION: Check all required coordinates exist and are numeric
          const hasAllCoords = 
            typeof attrsObj.semantic.x1 === 'number' &&
            typeof attrsObj.semantic.y1 === 'number' &&
            typeof attrsObj.semantic.x2 === 'number' &&
            typeof attrsObj.semantic.y2 === 'number' &&
            typeof attrsObj.position.x === 'number' &&
            typeof attrsObj.position.y === 'number';
          
          if (hasAllCoords) {
            // Convert relative to absolute for AI consumption
            const absoluteSemantic = {
              ...attrsObj.semantic,
              x1: attrsObj.semantic.x1 + attrsObj.position.x,
              y1: attrsObj.semantic.y1 + attrsObj.position.y,
              x2: attrsObj.semantic.x2 + attrsObj.position.x,
              y2: attrsObj.semantic.y2 + attrsObj.position.y
            };
            
            // ✅ POST-VALIDATION: Ensure results are valid finite numbers
            if (
              Number.isFinite(absoluteSemantic.x1) &&
              Number.isFinite(absoluteSemantic.y1) &&
              Number.isFinite(absoluteSemantic.x2) &&
              Number.isFinite(absoluteSemantic.y2)
            ) {
              console.debug('[BaseApplication] 🔄 Converted line/arrow coords to absolute for AI:', {
                position: { x: attrsObj.position.x, y: attrsObj.position.y },
                relativeStored: { 
                  x1: attrsObj.semantic.x1, 
                  y1: attrsObj.semantic.y1, 
                  x2: attrsObj.semantic.x2, 
                  y2: attrsObj.semantic.y2 
                },
                absoluteForAI: { 
                  x1: absoluteSemantic.x1, 
                  y1: absoluteSemantic.y1, 
                  x2: absoluteSemantic.x2, 
                  y2: absoluteSemantic.y2 
                }
              });
              
              attrsObj.semantic = absoluteSemantic;
            } else {
              console.error('[BaseApplication] ❌ Invalid coordinates after conversion (NaN/Infinity):', {
                absoluteSemantic,
                position: attrsObj.position,
                semantic: attrsObj.semantic
              });
            }
          } else {
            console.warn('[BaseApplication] ⚠️ Incomplete or non-numeric coordinates for line/arrow, skipping conversion:', {
              semantic: attrsObj.semantic,
              position: attrsObj.position,
              className: serialized.className
            });
          }
        } catch (e) {
          console.error('[BaseApplication] ❌ Error converting line/arrow coordinates:', e, {
            semantic: attrsObj.semantic,
            position: attrsObj.position
          });
        }
      }
      
      // Include any other non-standard attributes (excluding base fields)
      const baseFields = ['id', 'className', 'position', 'style', 'semantic'];
      for (const [key, value] of Object.entries(attrs)) {
        if (!baseFields.includes(key) && value !== undefined) {
          // Try to parse as JSON if it looks like JSON
          if (typeof value === 'string' && (value.startsWith('{') || value.startsWith('['))) {
            try {
              attrsObj[key] = JSON.parse(value);
            } catch (e) {
              attrsObj[key] = value;
            }
          } else {
            attrsObj[key] = value;
          }
        }
      }
      
      // Add attrs if we have any
      if (Object.keys(attrsObj).length > 0) {
        serialized.attrs = attrsObj;
      }
      
      return serialized;
    }
    
    return node;
  }
  
  /**
   * Calculate word count
   */
  protected calculateWordCount(nodes: any[]): number {
    let count = 0;
    
    for (const node of nodes) {
      if (node.text) {
        count += node.text.split(/\s+/).length;
      }
    }
    
    return count;
  }
  
  /**
   * Get loading progress
   */
  getLoadingProgress() {
    return { ...this.loadingProgress };
  }
  
  /**
   * Get loading phase
   */
  getLoadingPhase(): LoadingPhase {
    return this.loadingPhase;
  }
  
  /**
   * Event emitter - on
   */
  on(event: string, handler: Function): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    this.eventHandlers.get(event)!.add(handler);
  }
  
  /**
   * Event emitter - off
   */
  off(event: string, handler: Function): void {
    this.eventHandlers.get(event)?.delete(handler);
  }
  
  /**
   * Event emitter - emit
   */
  protected emit(event: string, ...args: any[]): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      for (const handler of handlers) {
        try {
          handler(...args);
        } catch (error) {
          console.error('[BaseApplication] Event handler error:', error);
        }
      }
    }
  }
  
  // ========================================================================
  // Collaboration Support (Y.js)
  // ========================================================================
  
  /**
   * Collaboration state
   * ✅ PROTECTED: So child classes can access
   */
  protected collaborationProvider?: any; // YjsCollaborationProvider
  protected prosemirrorBinding?: any; // ProseMirrorYjsBinding
  protected undoRedoManager?: any; // UndoRedoManager
  
  /**
   * Enable collaboration for this application
   * Should be called after editor is initialized
   */
  async enableCollaboration(config: {
    documentId: string;
    user: { id: string; name: string; color: string };
    websocketUrl?: string;
    authToken?: string;
  }): Promise<void> {
    // Guard against duplicate calls — reuse the live provider rather than creating a second one
    if (this.collaborationProvider && !(this.collaborationProvider as any).isDestroyed) {
      console.warn('[BaseApplication] Collaboration already active for', config.documentId, '— skipping duplicate setup');
      return;
    }

    console.log('[BaseApplication] Enabling collaboration:', config.documentId);
    
    try {
      // Import collaboration modules dynamically
      const { createCollaborationProvider } = await import('../collaboration');
      const { createProseMirrorYjsBinding } = await import('./yjs');
      const { createUndoRedoManager } = await import('./yjs');
      
      // Create collaboration provider
      this.collaborationProvider = createCollaborationProvider({
        documentId: config.documentId,
        user: config.user,
        websocketUrl: config.websocketUrl,
        authToken: config.authToken,
      });
      
      // Connect
      this.collaborationProvider.connect();
      
      // If we have an editor view, bind Y.js to ProseMirror
      if (this.editorView) {
        this.prosemirrorBinding = createProseMirrorYjsBinding({
          view: this.editorView,
          provider: this.collaborationProvider,
          user: config.user,
          undoRedo: true,
        });
        
        // Create undo/redo manager
        this.undoRedoManager = createUndoRedoManager({
          view: this.editorView,
          keyboardShortcuts: true,
        });
      }
      
      // Emit collaboration enabled event
      this.emit('collaboration-enabled', this.collaborationProvider);
      
      console.log('[BaseApplication] ✅ Collaboration enabled');
      
    } catch (error) {
      console.error('[BaseApplication] Failed to enable collaboration:', error);
      throw error;
    }
  }
  
  /**
   * Disable collaboration
   */
  disableCollaboration(): void {
    console.log('[BaseApplication] Disabling collaboration');
    
    // Destroy undo/redo manager
    if (this.undoRedoManager) {
      this.undoRedoManager.destroy();
      this.undoRedoManager = undefined;
    }
    
    // Destroy ProseMirror binding
    if (this.prosemirrorBinding) {
      this.prosemirrorBinding.destroy();
      this.prosemirrorBinding = undefined;
    }
    
    // Destroy provider
    if (this.collaborationProvider) {
      this.collaborationProvider.destroy();
      this.collaborationProvider = undefined;
    }
    
    this.emit('collaboration-disabled');
    console.log('[BaseApplication] ✅ Collaboration disabled');
  }
  
  /**
   * Get collaboration provider
   */
  getCollaborationProvider(): any {
    return this.collaborationProvider;
  }
  
  /**
   * Check if collaboration is enabled
   */
  isCollaborationEnabled(): boolean {
    return !!this.collaborationProvider;
  }

  /**
   * Get WebSocket URL for collaboration
   */
  protected getWebSocketUrl(): string {
    return WEBSOCKET_URL;
  }

  /**
   * WebSocket room prefix for this application type.
   * Subclasses override to match the WS server's persistence routing (e.g. 'note', 'page').
   */
  protected getWebSocketRoomPrefix(): string {
    return 'doc';
  }

  /**
   * Upgrade an offline instance to online by attaching a WebSocket collaboration provider
   * to the existing Y.Doc without replacing it. Safe to call multiple times — no-op if already online.
   * Used by UI components when they adopt an offline registry instance.
   */
  async ensureCollaboration(entityId: string, documentId?: string): Promise<void> {
    const requestedDocumentId = documentId ?? `${this.getWebSocketRoomPrefix()}:${entityId}`;

    console.log(`[BaseApplication] ensureCollaboration: room=${requestedDocumentId} entityId=${entityId.slice(-6)}`)

    if (this.collaborationProvider) {
      const currentDocId = this.collaborationProvider.getDocumentId();
      if (currentDocId === requestedDocumentId) return; // already on correct room
      // Wrong room — destroy and reconnect below
      this.collaborationProvider.destroy();
      this.collaborationProvider = null;
    }

    if (!this.ydoc) {
      console.warn('[BaseApplication] ensureCollaboration: no Y.Doc, skipping');
      return;
    }

    console.log('[BaseApplication] 🔌 Upgrading offline instance to online for:', entityId);

    const { createCollaborationProvider } = await import('../collaboration');
    const { authService } = await import('../auth/authService');
    const { generateUserColor } = await import('../../apps/shared/appHelpers');

    const authUser = authService.getCurrentUser();
    if (!authUser) {
      console.warn('[BaseApplication] ensureCollaboration: not authenticated, skipping');
      return;
    }

    this.collaborationProvider = createCollaborationProvider({
      documentId: requestedDocumentId,
      user: {
        id: authUser.id,
        name: authUser.name || authUser.email || 'User',
        color: generateUserColor(authUser.id),
      },
      websocketUrl: this.getWebSocketUrl(),
      authToken: '',
      offline: false,
      doc: this.ydoc,
    });

    this.collaborationProvider.connect();

    await new Promise<void>((resolve) => {
      const timeout = setTimeout(() => {
        console.warn('[BaseApplication] ensureCollaboration resolved via: timeout for:', entityId);
        resolve();
      }, 5000);
      const onDone = (reason: string) => () => {
        console.log('[BaseApplication] ensureCollaboration resolved via:', reason, 'for:', entityId);
        clearTimeout(timeout);
        resolve();
      };
      this.collaborationProvider!.once('synced', onDone('synced'));
      this.collaborationProvider!.once('error', onDone('error'));
    });

    console.log('[BaseApplication] ✅ Online collaboration ready', {
      entityId: entityId.slice(-8),
      room: requestedDocumentId,
      status: this.collaborationProvider?.status,
      prefix: this.getWebSocketRoomPrefix(),
    });
  }
  
  /**
   * Perform undo
   */
  undo(): boolean {
    if (this.undoRedoManager) {
      return this.undoRedoManager.undo();
    }
    return false;
  }
  
  /**
   * Perform redo
   */
  redo(): boolean {
    if (this.undoRedoManager) {
      return this.undoRedoManager.redo();
    }
    return false;
  }
  
  /**
   * Destroy application (cleanup)
   * ✅ FIXED: Now properly awaits final save before cleanup
   */
  async destroy(): Promise<void> {
    console.log('[BaseApplication] Destroying with persistence cleanup');
    
    // ✅ FIX (Bug #1): Await final save before cleanup
    if (this.ydoc && this.entityId && this.autoSaveEnabled) {
      console.log('[BaseApplication] 💾 Forcing final save...');
      
      try {
        // ✅ CRITICAL: Await the save to ensure it completes
        await this.forceSave();
        console.log('[BaseApplication] ✅ Final save complete');
      } catch (error) {
        console.error('[BaseApplication] ❌ Final save failed:', error);
        // Continue with cleanup even if save fails
      }
    }
    
    // ✅ NEW: Cleanup persistence resources (safe now that save is complete)
    this.cleanupPersistence();
    
    // Disable collaboration first
    if (this.isCollaborationEnabled()) {
      this.disableCollaboration();
    }
    
    // ✅ FIX: Destroy undo/redo manager
    if (this.undoRedoManager) {
      this.undoRedoManager.destroy();
      this.undoRedoManager = undefined;
    }
    
    // ✅ FIX: Clean up Y.Doc (remove all event listeners and destroy)
    if (this.ydoc) {
      // Remove any registered event listeners
      // Note: Y.js will clean up internal listeners when destroyed
      this.ydoc.destroy();
      this.ydoc = undefined;
    }
    
    // Clear Y.xml reference
    if (this.yxml) {
      this.yxml = undefined;
    }
    
    // Clear indices
    this.byIdIndex.clear();
    this.byClassIndex.clear();
    this.fullTextIndex.clear();
    
    // Clear event handlers
    this.eventHandlers.clear();
    
    // Destroy editor view
    if (this.editorView) {
      this.editorView.destroy();
      this.editorView = undefined;
    }
    
    // Clear editor state
    if (this.editorState) {
      this.editorState = undefined;
    }
    
    console.log('[BaseApplication] ✅ Destroyed');
  }
  
  // ========================================================================
  // Y.js Transaction Methods (for entity_write)
  // ========================================================================
  
  /**
   * Execute single operation (for batch)
   */
  private executeSingleOperation(op: any): string[] {
    const operation = op.operation || op.op;
    
    switch (operation) {
      case 'insert': return this.executeInsert(op);
      case 'replace': return this.executeReplace(op);
      case 'delete':
      case 'remove': return this.executeDelete(op);
      case 'update': return this.executeUpdate(op);
      default: 
        console.warn('[BaseApplication] Unknown operation in batch:', operation);
        return [];
    }
  }
  
  /**
   * Count slides directly from Y.xml (bypasses index - works in transactions)
   */
  private countSlidesDirectly(): number {
    if (!this.yxml) return 0;
    
    let count = 0;
    for (let i = 0; i < this.yxml.length; i++) {
      const elem = this.yxml.get(i);
      
      // ✅ FIX: Check if elem is Y.XmlElement before calling getAttribute
      if (!(elem instanceof Y.XmlElement)) continue;
      
      const className = elem.getAttribute('className');
      if (className === 'slide') {
        count++;
      }
    }
    
    console.log('[BaseApplication] 🔍 Direct slide count:', count);
    return count;
  }
  
  /**
   * Find slide by number directly from Y.xml (bypasses index - works in transactions)
   */
  private findSlideByNumberDirectly(slideNumber: number): string | null {
    if (!this.yxml) return null;
    
    let slideCount = 0;
    for (let i = 0; i < this.yxml.length; i++) {
      const elem = this.yxml.get(i);
      
      // ✅ FIX: Check if elem is Y.XmlElement before calling getAttribute
      if (!(elem instanceof Y.XmlElement)) continue;
      
      const className = elem.getAttribute('className');
      if (className === 'slide') {
        slideCount++;
        if (slideCount === slideNumber) {
          return elem.getAttribute('id') || null;
        }
      }
    }
    
    return null;
  }
  
  /**
   * Execute INSERT operation
   */
  protected executeInsert(params: any): string[] {
    if (!this.yxml) return [];
    
    let content = params.content || [];
    const target = params.target || {};
    const affectedIds: string[] = [];
    
    // ✅ Handle string content - Support both plain strings and JSON strings
    // Plain strings are automatically converted to className format for compatibility
    if (typeof content === 'string') {
      try {
        // Try to parse as JSON string (AI might send stringified objects)
        const parsed = JSON.parse(content);
        content = parsed;
        console.log('[BaseApplication] ✅ Parsed JSON string content:', {
          type: Array.isArray(parsed) ? 'array' : 'object',
          length: Array.isArray(parsed) ? parsed.length : 1,
          firstClassName: Array.isArray(parsed) ? parsed[0]?.className : parsed.className
        });
      } catch (e) {
        // Not JSON - plain text string, parse for block-level markdown first!
        console.log('[BaseApplication] Plain text string detected, parsing for block markdown:', {
          contentLength: content.length,
          contentPreview: content.substring(0, 100)
        });
        
        // ✅ CRITICAL FIX: Parse block-level markdown and split into multiple nodes!
        content = this.parseBlockMarkdownToNodes(content);
        
        console.log('[BaseApplication] ✅ Parsed block markdown into nodes:', {
          nodeCount: content.length,
          nodeTypes: content.map((n: any) => n.className || n.type).join(', ')
        });
      }
    }
    
    // Ensure content is an array
    if (!Array.isArray(content)) {
      content = [content];
    }
    
    // ✅ CRITICAL FIX: Check if any note-paragraph contains block markdown!
    // AI often sends: {className: "note-paragraph", attrs: {text: "# Heading\n\nParagraph"}}
    // We need to expand these into multiple nodes AFTER JSON parsing
    const expandedContent: any[] = [];
    for (const item of content) {
      if ((item.className === 'note-paragraph' || !item.className) && 
          item.attrs?.text && 
          typeof item.attrs.text === 'string') {
        
        const text = item.attrs.text;
        
        // Check if text contains block markdown or multiple lines.
        // Single \n is included: in notes each line should be a separate paragraph,
        // and storing \n inside Y.XmlText causes it to be collapsed as whitespace in HTML.
        const hasBlockMarkdown = /^#{1,6}\s/m.test(text) || text.includes('\n') || /^[-*+]\s/m.test(text) || /^\d+\.\s/m.test(text) || /^```/m.test(text) || /^>/m.test(text);
        
        if (hasBlockMarkdown) {
          console.log('[BaseApplication] 🔍 Detected block markdown in paragraph, expanding:', {
            originalLength: text.length,
            preview: text.substring(0, 100)
          });
          
          // Parse this paragraph's text as block markdown
          const parsedNodes = this.parseBlockMarkdownToNodes(text);
          
          console.log('[BaseApplication] ✅ Expanded 1 paragraph into', parsedNodes.length, 'nodes:', {
            nodeTypes: parsedNodes.map(n => n.className || n.type).join(', ')
          });
          
          expandedContent.push(...parsedNodes);
        } else {
          // No block markdown, keep as-is
          expandedContent.push(item);
        }
      } else {
        // Not a note-paragraph or no text, keep as-is
        expandedContent.push(item);
      }
    }
    
    content = expandedContent;
    
    // Generate revision ID for change tracking
    const revisionId = this.generateRevisionId();
    
    // Convert content to Y.XmlElements and apply change tracking
    const elements = content.map((item: any) => {
      const element = this.contentToYXmlElement(item);
      
      // ✅ CHANGE TRACKING: Mark new elements for user review
      if (this.suggestionMode) {
        this.applyChangeTracking(element, 'insert', revisionId);
      }
      
      return element;
    });
    
    // Determine insertion position
    let insertIndex = 0;
    
    if (target.at === 'end') {
      insertIndex = this.yxml.length;
    } else if (target.at === 'start') {
      insertIndex = 0;
    } else if (target.after) {
      insertIndex = this.findNodeIndexById(target.after) + 1;
    } else if (target.before) {
      insertIndex = this.findNodeIndexById(target.before);
    } else {
      insertIndex = this.yxml.length; // Default to end
    }
    
    // Insert elements in Y.js transaction (already in transaction from handleEntityWrite)
    this.yxml.insert(insertIndex, elements);
    
    // Collect IDs
    for (const element of elements) {
      const id = element.getAttribute('id');
      if (id) affectedIds.push(id);
    }
    
    console.log('[BaseApplication] Inserted', affectedIds.length, 'nodes at index', insertIndex, {
      suggestionMode: this.suggestionMode,
      revisionId: this.suggestionMode ? revisionId.slice(-12) : 'N/A'
    });
    
    return affectedIds;
  }
  
  /**
   * Execute REPLACE operation
   */
  protected executeReplace(params: any): string[] {
    if (!this.yxml) return [];
    
    const target = params.target || {};
    let content = params.content || [];
    const affectedIds: string[] = [];
    
    // ✅ CRITICAL FIX: Handle string content
    // First, try to parse as JSON (for Canvas/Presentations shapes)
    // If parsing fails, treat as plain text (for Notes)
    if (typeof content === 'string') {
      try {
        // Try to parse as JSON
        const parsed = JSON.parse(content);
        content = parsed;
        console.log('[BaseApplication] ✅ Parsed JSON string content (replace):', {
          type: Array.isArray(parsed) ? 'array' : 'object',
          length: Array.isArray(parsed) ? parsed.length : 1,
          firstClassName: Array.isArray(parsed) ? parsed[0]?.className : parsed.className
        });
      } catch (e) {
        // Not JSON - treat as plain text for Notes
        console.log('[BaseApplication] Converting plain text to paragraph (replace):', {
          contentLength: content.length,
          contentPreview: content.substring(0, 100)
        });
        
        content = [{
          className: 'note-paragraph',
          attrs: { text: content }
        }];
      }
    }
    
    // Ensure content is an array
    if (!Array.isArray(content)) {
      content = [content];
    }
    
    // Generate revision ID for this replace operation
    const revisionId = this.generateRevisionId();
    
    // Find nodes to replace
    const nodeIds = this.findNodesByTarget(target);
    
    // ✅ CRITICAL DEBUG: Log target matching details
    console.log('[BaseApplication] Replace target resolution:', {
      targetKeys: Object.keys(target),
      target,
      foundNodes: nodeIds.length,
      nodeIds: nodeIds.slice(0, 3),
      suggestionMode: this.suggestionMode
    });
    
    if (this.suggestionMode) {
      // ✅ SUGGESTION MODE: Mark old as 'old', create new as 'new', show both for review
      
      // Step 1: Mark ALL old elements for deletion (DON'T delete yet!)
      for (const nodeId of nodeIds) {
        const index = this.findNodeIndexById(nodeId);
        if (index >= 0) {
          const oldElement = this.yxml.get(index) as Y.XmlElement;
          
          // Mark for deletion
          this.applyChangeTracking(oldElement, 'delete', revisionId);
          affectedIds.push(nodeId);
        }
      }
      
      // Step 2: Create new elements with change tracking
      const newElements = content.map((item: any) => {
        const element = this.contentToYXmlElement(item);
        this.applyChangeTracking(element, 'insert', revisionId);
        return element;
      });
      
      // Step 3: Insert new elements AFTER the last old element
      // This way user sees: [OLD (red)] [NEW (green)] [Accept/Reject buttons]
      if (nodeIds.length > 0) {
        const lastOldIndex = this.findNodeIndexById(nodeIds[nodeIds.length - 1]);
        
        // ✅ BUG FIX: Validate index before insertion
        if (lastOldIndex >= 0) {
          this.yxml.insert(lastOldIndex + 1, newElements);
          
          for (const element of newElements) {
            const id = element.getAttribute('id');
            if (id) affectedIds.push(id);
          }
        } else {
          console.error('[BaseApplication] Replace failed: Could not find last old element index');
        }
      } else {
        // ✅ CRITICAL FIX: Throw error instead of fallback - safer for structured apps
        // Falling back to INSERT can corrupt document structure (e.g., slides, sheets)
        console.error('[BaseApplication] Replace found 0 target nodes - throwing error');
        throw new Error('Replace operation failed: No target nodes found matching selector. Use insert operation to add new content.');
      }
      
      console.log('[BaseApplication] Replace (suggestion mode):', {
        markedOld: nodeIds.length,
        insertedNew: newElements.length,
        revisionId: revisionId.slice(-12),
        hadFallback: nodeIds.length === 0
      });
      
    } else {
      // ✅ DIRECT MODE: Immediate replacement (no tracking)
      for (const nodeId of nodeIds) {
        const index = this.findNodeIndexById(nodeId);
        if (index >= 0) {
          // Delete old node immediately
          this.yxml.delete(index, 1);
          affectedIds.push(nodeId);
          
          // Insert new content at same position
          const elements = content.map((item: any) => this.contentToYXmlElement(item));
          this.yxml.insert(index, elements);
          
          for (const element of elements) {
            const id = element.getAttribute('id');
            if (id) affectedIds.push(id);
          }
        }
      }
      
      console.log('[BaseApplication] Replace (direct mode):', {
        replaced: affectedIds.length
      });
    }
    
    return affectedIds;
  }
  
  /**
   * Execute DELETE operation (remove nodes)
   * ✅ ALWAYS DELETES - suggestionMode only affects change tracking, not deletion behavior
   */
  protected executeDelete(params: any, skipTransaction: boolean = false): string[] {
    if (!this.yxml || !this.ydoc) return [];
    
    const target = params.target || {};
    const affectedIds: string[] = [];
    
    console.log('[BaseApplication] 🗑️ Delete operation:', {
      targetKeys: Object.keys(target),
      target,
      suggestionMode: this.suggestionMode,
      appType: this.constructor.name
    });
    
    // Find nodes to delete
    const nodeIds = this.findNodesByTarget(target);
    
    console.log('[BaseApplication] 🗑️ Found nodes to delete:', {
      count: nodeIds.length,
      nodeIds: nodeIds.slice(0, 5),
      indexSizes: {
        byId: this.byIdIndex.size,
        byClass: Array.from(this.byClassIndex.keys())
      }
    });
    
    if (nodeIds.length === 0) {
      console.warn('[BaseApplication] ⚠️ No nodes found matching target:', target);
      return [];
    }
    
    // ✅ ALWAYS DELETE (regardless of suggestionMode)
    // suggestionMode only affects change tracking/logging, not whether deletions happen
    
    // Get indices and sort in reverse (to maintain index validity during deletion)
    const indices = nodeIds
      .map(id => ({ id, index: this.findNodeIndexById(id) }))
      .filter(item => item.index >= 0)
      .sort((a, b) => b.index - a.index);
    
    console.log('[BaseApplication] 🗑️ Deleting in reverse order:', {
      count: indices.length,
      indices: indices.map(i => ({ id: i.id, index: i.index })).slice(0, 5)
    });
    
    // ✅ CRITICAL FIX: Capture references and clientID before transaction
    const yxml = this.yxml;
    const ydoc = this.ydoc;
    if (!ydoc || !yxml) {
      throw new Error('[BaseApplication] Y.js not initialized - cannot delete');
    }
    const clientID = ydoc.clientID;
    
    // ✅ FIX: Only wrap in transaction if not already in one (prevents nested transactions in batch)
    if (skipTransaction) {
      // Already in transaction (e.g., called from batch operation)
      // Delete directly without creating nested transaction
      for (const item of indices) {
        console.log('[BaseApplication] 🗑️ Deleting element:', {
          id: item.id,
          index: item.index
        });
        
        yxml.delete(item.index, 1);
        affectedIds.push(item.id);
      }
    } else {
      // Wrap in transaction for atomic operation and proper undo/redo support
      ydoc.transact(() => {
        // Delete in reverse order (high to low indices)
        // This ensures indices remain valid - deleting from end doesn't affect earlier indices
        for (const item of indices) {
          console.log('[BaseApplication] 🗑️ Deleting element:', {
            id: item.id,
            index: item.index
          });
          
          yxml.delete(item.index, 1);
          affectedIds.push(item.id);
        }
      }, clientID);
    }
    
    console.log('[BaseApplication] ✅ Successfully deleted:', {
      count: affectedIds.length,
      ids: affectedIds.slice(0, 5)
    });
    
    // Optional: Emit event for change tracking (if suggestionMode enabled)
    if (this.suggestionMode) {
      const revisionId = this.generateRevisionId();
      this.emit('elements:deleted', {
        ids: affectedIds,
        revisionId,
        deletedBy: 'ai',
        timestamp: Date.now()
      });
      console.log('[BaseApplication] 📝 Change tracking event emitted:', {
        revisionId: revisionId.slice(-12),
        count: affectedIds.length
      });
    }
    
    return affectedIds;
  }
  
  /**
   * Execute UPDATE operation (modify attributes)
   */
  private executeUpdate(params: any): string[] {
    if (!this.yxml) return [];
    
    const target = params.target || {};
    const attrs = params.attrs || {};
    const affectedIds: string[] = [];
    
    console.log('[BaseApplication] 🔧 executeUpdate:', {
      target,
      attrs,
      attrsKeys: Object.keys(attrs)
    });
    
    // Find nodes to update
    const nodeIds = this.findNodesByTarget(target);
    
    console.log('[BaseApplication] 🔍 Found nodes to update:', {
      count: nodeIds.length,
      nodeIds: nodeIds.slice(0, 3)
    });
    
    // Update each node's attributes
    for (const nodeId of nodeIds) {
      const index = this.findNodeIndexById(nodeId);
      
      console.log('[BaseApplication] 🔧 Updating node:', {
        nodeId,
        index,
        found: index >= 0
      });
      
      if (index >= 0) {
        const element = this.yxml.get(index) as Y.XmlElement;
        
        console.log('[BaseApplication] 🔍 Element before update:', {
          nodeName: (element as any).nodeName,
          currentAttrs: (element as any).getAttributes ? 
            Object.fromEntries(Array.from((element as any).getAttributes()) as [string, any][]) : 
            {}
        });
        
        // Update attributes
        if (attrs.className !== undefined) {
          element.setAttribute('className', attrs.className);
        }
        if (attrs.style !== undefined) {
          element.setAttribute('style', attrs.style);
        }
        if (attrs.position !== undefined) {
          element.setAttribute('position', attrs.position);
        }
        
        // ✅ CRITICAL FIX: Handle text content update specially!
        // Text is NOT an attribute - it's nested content in Y.XmlText nodes
        if (attrs.text !== undefined) {
          const newText = attrs.text;
          
          // REFACTORED: 2-level structure (paragraph → text)
          // Simply replace all children with new text
          while (element.length > 0) {
            element.delete(0, 1);
          }
          
          // ✅ FIX: Parse markdown when updating text too!
          const formattedText = this.parseMarkdownToYText(newText);
          element.insert(0, [formattedText]);
          
          console.log('[BaseApplication] ✅ Updated text content with markdown parsing:', {
            nodeId,
            textLength: newText.length,
            hasFormatting: newText.includes('**') || newText.includes('*') || newText.includes('`')
          });
        }
        
        // Update any other attributes (NOT including text!)
        Object.keys(attrs).forEach(key => {
          if (!['className', 'style', 'position', 'text'].includes(key)) {
            element.setAttribute(key, attrs[key]);
          }
        });
        
        affectedIds.push(nodeId);
      }
    }
    
    console.log('[BaseApplication] Updated', affectedIds.length, 'nodes');
    
    return affectedIds;
  }
  
  /**
   * Convert content object to Y.XmlElement
   * Requires className-based content: { className: "shape-rectangle", attrs: {...} }
   */
  private contentToYXmlElement(item: any): Y.XmlElement {
    if (!item.className) {
      throw new Error('className is required for all content. Use className-based pattern: { className: "...", attrs: { position, semantic, style } }');
    }
    
    return this.classNameToYXmlElement(item);
  }
  
  /**
   * Helper: Set flat positioning/styling attributes
   */
  private setFlatAttributes(element: Y.XmlElement, attrs: any = {}) {
    if (attrs.className) element.setAttribute('className', attrs.className);
    if (attrs.x !== undefined) element.setAttribute('x', attrs.x);
    if (attrs.y !== undefined) element.setAttribute('y', attrs.y);
    if (attrs.width !== undefined) element.setAttribute('width', attrs.width);
    if (attrs.height !== undefined) element.setAttribute('height', attrs.height);
    if (attrs.rotation !== undefined) element.setAttribute('rotation', attrs.rotation);
    if (attrs.zIndex !== undefined) element.setAttribute('zIndex', attrs.zIndex);
    if (attrs.style) element.setAttribute('style', JSON.stringify(attrs.style));
    if (attrs.change_status) element.setAttribute('change_status', attrs.change_status);
    if (attrs.revision_id) element.setAttribute('revision_id', attrs.revision_id);
    if (attrs.diff_type) element.setAttribute('diff_type', attrs.diff_type);
  }
  
  /**
   * Helper: Set structured attributes (handles nested position, semantic, style objects)
   * This is the ONLY pattern we support now - no backward compatibility needed
   */
  private setStructuredAttributes(element: Y.XmlElement, attrs: any = {}) {
    console.log('[BaseApplication] setStructuredAttributes called:', {
      hasPosition: !!attrs.position,
      positionType: typeof attrs.position,
      positionKeys: attrs.position ? Object.keys(attrs.position) : [],
      position: attrs.position,
      hasSemantic: !!attrs.semantic,
      hasStyle: !!attrs.style
    });
    
    // Handle nested position object
    if (attrs.position) {
      if (attrs.position.x !== undefined) element.setAttribute('x', attrs.position.x);
      if (attrs.position.y !== undefined) element.setAttribute('y', attrs.position.y);
      if (attrs.position.width !== undefined) element.setAttribute('width', attrs.position.width);
      if (attrs.position.height !== undefined) element.setAttribute('height', attrs.position.height);
      if (attrs.position.rotation !== undefined) element.setAttribute('rotation', attrs.position.rotation);
      if (attrs.position.z !== undefined) element.setAttribute('zIndex', attrs.position.z);
      
      // ✅ CRITICAL: Also store the complete position object as JSON for Canvas
      // Default to 'type: absolute' if not specified
      const positionWithType = {
        type: attrs.position.type || 'absolute',
        ...attrs.position
      };
      element.setAttribute('position', JSON.stringify(positionWithType));
      
      console.log('[BaseApplication] Set position attributes:', {
        x: attrs.position.x,
        y: attrs.position.y,
        width: attrs.position.width,
        height: attrs.position.height,
        positionJSON: JSON.stringify(positionWithType)
      });
    }
    
    // Handle nested semantic object (store as JSON string)
    if (attrs.semantic) {
      element.setAttribute('semantic', JSON.stringify(attrs.semantic));
      console.log('[BaseApplication] Set semantic attributes:', {
        semantic: attrs.semantic,
        semanticJSON: JSON.stringify(attrs.semantic)
      });
    }
    
    // Handle style (already JSON stringified or object)
    if (attrs.style) {
      const styleStr = typeof attrs.style === 'string' 
        ? attrs.style 
        : JSON.stringify(attrs.style);
      element.setAttribute('style', styleStr);
    }
    
    // Handle change tracking attributes
    if (attrs.change_status) element.setAttribute('change_status', attrs.change_status);
    if (attrs.revision_id) element.setAttribute('revision_id', attrs.revision_id);
    if (attrs.diff_type) element.setAttribute('diff_type', attrs.diff_type);
  }
  
  /**
   * Helper: Create element from className-based content (NEW pattern for Canvas/Presentations)
   * Handles content like: { className: "shape-rectangle", attrs: { position: {...}, style: {...} } }
   */
  private classNameToYXmlElement(item: any): Y.XmlElement {
    const className = item.className;
    const attrs = item.attrs || {};

    // ─── Lists ────────────────────────────────────────────────────────────────
    if (className === 'note-bullet-list' || className === 'note-ordered-list') {
      const listType = className === 'note-bullet-list' ? 'bullet_list' : 'ordered_list';
      const listElement = new Y.XmlElement(listType);
      listElement.setAttribute('id', attrs.id || this.generateId());
      listElement.setAttribute('className', className);

      const items: string[] = attrs.listItems || [];
      const listItemElements = items.map((itemText: string) => {
        const listItem = new Y.XmlElement('list_item');
        listItem.setAttribute('id', this.generateId());

        const para = new Y.XmlElement('paragraph');
        para.setAttribute('id', this.generateId());
        para.setAttribute('className', 'note-paragraph');
        para.insert(0, [this.parseMarkdownToYText(itemText)]);

        listItem.insert(0, [para]);
        return listItem;
      });

      if (listItemElements.length > 0) {
        listElement.insert(0, listItemElements);
      }

      console.log('[BaseApplication] Created list element:', { className, itemCount: items.length });
      return listElement;
    }

    // ─── Code Block ──────────────────────────────────────────────────────────
    if (className === 'note-code-block') {
      const codeElement = new Y.XmlElement('code_block');
      codeElement.setAttribute('id', attrs.id || this.generateId());
      codeElement.setAttribute('className', className);
      if (attrs.language) codeElement.setAttribute('language', attrs.language);

      const yText = new Y.XmlText();
      yText.insert(0, attrs.text || '');
      codeElement.insert(0, [yText]);

      console.log('[BaseApplication] Created code_block element:', { language: attrs.language });
      return codeElement;
    }

    // ─── Blockquote ──────────────────────────────────────────────────────────
    if (className === 'note-blockquote') {
      const bqElement = new Y.XmlElement('blockquote');
      bqElement.setAttribute('id', attrs.id || this.generateId());
      bqElement.setAttribute('className', className);

      const para = new Y.XmlElement('paragraph');
      para.setAttribute('id', this.generateId());
      para.insert(0, [this.parseMarkdownToYText(attrs.text || '')]);
      bqElement.insert(0, [para]);

      console.log('[BaseApplication] Created blockquote element');
      return bqElement;
    }

    // ─── Canvas v2 and all other types ───────────────────────────────────────
    // ✅ CRITICAL: Detect Canvas v2 elements - they use Pure JSON format!
    const isCanvasV2Element = 
      className.startsWith('shape-') ||
      className === 'canvas-image' ||
      className === 'canvas-text' ||
      className === 'sticky-note' ||
      className === 'canvas-button' ||
      className === 'canvas-group' ||
      className === 'connector';
    
    // Determine Y.js element type from className
    let elementType = 'paragraph'; // default fallback
    
    // ✅ Notes patterns - handle headings!
    if (className === 'note-heading' || item.type === 'heading') {
      elementType = 'heading';
    } else if (className === 'note-paragraph') {
      elementType = 'paragraph';
    }
    // Canvas patterns
    else if (className.startsWith('shape-')) {
      elementType = 'shape';
    } else if (className === 'canvas-image') {
      elementType = 'image';
    } else if (className === 'canvas-text' || className === 'sticky-note') {
      elementType = 'paragraph';
    } else if (className === 'canvas-button') {
      elementType = 'paragraph'; // button is rendered as paragraph with className
    } else if (className === 'canvas-group') {
      elementType = 'container';
    } else if (className === 'connector') {
      elementType = 'line';
    }
    // Presentation patterns
    else if (className.startsWith('slide-')) {
      if (className === 'slide-shape') {
        elementType = 'shape';
      } else if (className === 'slide-image') {
        elementType = 'image';
      } else if (className === 'slide') {
        elementType = 'container';
      } else if (className === 'slide-bullet-list') {
        elementType = 'bullet_list';
      } else if (className === 'slide-numbered-list') {
        elementType = 'ordered_list';
          } else {
        // slide-title, slide-subtitle, slide-text, etc. are paragraphs
        elementType = 'paragraph';
      }
    }
    
    // Create element with appropriate type
    const element = new Y.XmlElement(elementType);
    const elementId = attrs.id || this.generateId();
    element.setAttribute('id', elementId);
    element.setAttribute('className', className);
    
    // ✅ CANVAS V2: Use Pure JSON format (single 'data' attribute)
    if (isCanvasV2Element) {
      console.log('[BaseApplication] 🎨 Creating Canvas v2 element in Pure JSON format:', {
        className,
        elementType,
        hasPosition: !!attrs.position,
        hasSemantic: !!attrs.semantic,
        hasStyle: !!attrs.style
      });
      
      // Build complete data object for Pure JSON format
      const data: any = {
        id: elementId,
        type: this.classNameToCanvasType(className)
      };
      
      // Extract position
      if (attrs.position) {
        data.x = attrs.position.x ?? 0;
        data.y = attrs.position.y ?? 0;
        data.width = attrs.position.width ?? 100;
        data.height = attrs.position.height ?? 100;
        if (attrs.position.rotation !== undefined) data.rotation = attrs.position.rotation;
        if (attrs.position.z !== undefined) data.zIndex = attrs.position.z;
      }
      
      // Extract style properties
      if (attrs.style) {
        if (attrs.style.fill) data.fill = attrs.style.fill;
        if (attrs.style.stroke) data.stroke = attrs.style.stroke;
        if (attrs.style.strokeWidth !== undefined) data.strokeWidth = attrs.style.strokeWidth;
        if (attrs.style.opacity !== undefined) data.opacity = attrs.style.opacity;
        if (attrs.style.color) data.color = attrs.style.color;
        
        // ✅ IMPROVEMENT: Extract text styling from style object
        if (attrs.style.fontSize !== undefined) data.fontSize = attrs.style.fontSize;
        if (attrs.style.fontFamily) data.fontFamily = attrs.style.fontFamily;
        if (attrs.style.fontWeight) data.fontWeight = attrs.style.fontWeight;
        if (attrs.style.fontStyle) data.fontStyle = attrs.style.fontStyle;
        if (attrs.style.textAlign) data.textAlign = attrs.style.textAlign;
      }
      
      // ✅ IMPROVEMENT: Apply sensible defaults for lines/arrows
      if (data.type === 'line' || data.type === 'arrow') {
        if (data.strokeWidth === undefined) {
          data.strokeWidth = 2; // Default stroke width for visibility
          console.log('[BaseApplication] 📏 Applied default strokeWidth=2 for line/arrow');
        }
      }
      
      // Extract semantic properties
      if (attrs.semantic) {
        // Image properties
        if (attrs.semantic.src) data.src = attrs.semantic.src;
        if (attrs.semantic.alt) data.alt = attrs.semantic.alt;
        if (attrs.semantic.fileName) data.fileName = attrs.semantic.fileName;
        if (attrs.semantic.imageSource) data.imageSource = attrs.semantic.imageSource;
        if (attrs.semantic.prompt) data.prompt = attrs.semantic.prompt;
        if (attrs.semantic.query) data.query = attrs.semantic.query;
        
        // Line/arrow endpoints (stored as relative coordinates)
        if (attrs.semantic.x1 !== undefined) {
          data.x1 = attrs.semantic.x1;
          data.y1 = attrs.semantic.y1;
          data.x2 = attrs.semantic.x2;
          data.y2 = attrs.semantic.y2;
        }
        
        // Shape properties
        if (attrs.semantic.shapeType) data.shapeType = attrs.semantic.shapeType;
        if (attrs.semantic.path) data.path = attrs.semantic.path;
        
        // ✅ IMPROVEMENT: Copy any other semantic properties we might have missed
        // This ensures forward compatibility with new features
        const knownSemanticKeys = ['src', 'alt', 'fileName', 'imageSource', 'prompt', 'query',
                                     'x1', 'y1', 'x2', 'y2', 'shapeType', 'path', 'anchoredTo'];
        Object.keys(attrs.semantic).forEach(key => {
          if (!knownSemanticKeys.includes(key) && attrs.semantic[key] !== undefined) {
            data[key] = attrs.semantic[key];
            console.log(`[BaseApplication] 📋 Copied additional semantic property: ${key}`);
          }
        });
      }
      
      // Extract text content
      if (attrs.text !== undefined) {
        data.text = attrs.text;
      }
      
      // Text styling - check both top level AND style object
      // Top level takes precedence (more specific)
      data.fontSize = attrs.fontSize || attrs.style?.fontSize;
      data.fontFamily = attrs.fontFamily || attrs.style?.fontFamily;
      data.fontWeight = attrs.fontWeight || attrs.style?.fontWeight;
      data.fontStyle = attrs.fontStyle || attrs.style?.fontStyle;
      data.textAlign = attrs.textAlign || attrs.style?.textAlign;
      
      // ✅ IMPROVEMENT: Handle z-index from multiple locations
      data.zIndex = attrs.zIndex || attrs.position?.zIndex || attrs.position?.z;
      
      // ✅ VALIDATION: Ensure required fields are present
      if (!data.id || !data.type) {
        throw new Error(`[BaseApplication] Pure JSON validation failed: missing required fields (id: ${data.id}, type: ${data.type})`);
      }
      
      // ✅ VALIDATION: Ensure position values are valid numbers
      if (isNaN(data.x) || isNaN(data.y) || isNaN(data.width) || isNaN(data.height)) {
        throw new Error(`[BaseApplication] Pure JSON validation failed: invalid position values (x: ${data.x}, y: ${data.y}, width: ${data.width}, height: ${data.height})`);
      }
      
      // ✅ VALIDATION: Check for required element-specific fields
      if (data.type === 'text' && !data.text) {
        console.warn(`[BaseApplication] ⚠️ Text element ${data.id} has no text content`);
      }
      if ((data.type === 'line' || data.type === 'arrow') && (data.x1 === undefined || data.x2 === undefined)) {
        console.warn(`[BaseApplication] ⚠️ Line/arrow element ${data.id} missing endpoint coordinates`);
      }
      if (data.type === 'image' && !data.src && !data.imageSource) {
        console.warn(`[BaseApplication] ⚠️ Image element ${data.id} has no src or imageSource`);
      }
      
      // Store as Pure JSON (single 'data' attribute)
      const dataJSON = JSON.stringify(data);
      console.log('[BaseApplication] 🔍 Storing data attribute:', {
        elementId,
        jsonLength: dataJSON.length,
        jsonPreview: dataJSON.substring(0, 200),
        jsonType: typeof dataJSON
      });
      element.setAttribute('data', dataJSON);
      
      console.log('[BaseApplication] ✅ Created Pure JSON element:', {
        id: elementId,
        type: data.type,
        dataKeys: Object.keys(data),
        jsonLength: dataJSON.length,
        preview: dataJSON.substring(0, 100)
      });
      
      return element;
    }
    
    // ✅ NON-CANVAS: Use traditional hybrid format (for Notes, Docs, etc.)
    // ✅ Handle heading level attribute
    if (elementType === 'heading' && attrs.level) {
      element.setAttribute('level', attrs.level);
    }
    
    // Handle special cases that need additional attributes
    if (elementType === 'shape' && attrs.semantic?.shapeType) {
      element.setAttribute('shapeType', attrs.semantic.shapeType);
    }
    
    if (elementType === 'image' && attrs.semantic?.src) {
      element.setAttribute('src', attrs.semantic.src);
      if (attrs.semantic.alt) element.setAttribute('alt', attrs.semantic.alt);
    }
    
    // ✅ REMOVED: Legacy auto-calculation for lines/arrows
    // This was causing DOUBLE TRANSFORMATION bug!
    // entityWriteCore.ts already handles start/end → position+semantic transformation
    // We should NOT transform again here!
    
    // Apply structured attributes (position, semantic, style)
    this.setStructuredAttributes(element, attrs);
    
    // Handle text content - support both patterns:
    // 1. attrs.text (RECOMMENDED - universal pattern for all text)
    // 2. item.content (legacy fallback for backward compatibility)
    let textContent = null;
    if (attrs.text != null) {
      // PRIMARY: attrs.text is the universal pattern for Notes, Canvas text, Presentation text
      textContent = String(attrs.text);
    } else if (item.content && typeof item.content === 'string') {
      // FALLBACK: Support legacy item.content for backward compatibility
      textContent = item.content;
    }
    
    if (textContent !== null) {
      // ✅ CRITICAL: Different apps use different text storage patterns!
      // Notes/Docs: ProseMirror pattern - text stored as Y.XmlText children
      // Slides: Store text as FLAT ATTRIBUTE
      
      const isSlideText = 
        className === 'slide-title' ||
        className === 'slide-subtitle' ||
        className === 'slide-text' ||
        className === 'slide-bullet-list' ||
        className === 'slide-numbered-list';
      
      if (isSlideText) {
        // Slides: Store text as FLAT ATTRIBUTE
        element.setAttribute('text', textContent);
        console.log('[BaseApplication] 📦 Stored text as flat attribute for Slides:', {
          className,
          textLength: textContent.length,
          preview: textContent.substring(0, 50)
        });
      } else {
        // Notes/Docs: Store text as Y.XmlText children (ProseMirror pattern).
        // Block-level markdown (# headings, - lists) should already have been expanded
        // into separate nodes by executeInsert → parseBlockMarkdownToNodes before reaching here.
        // This function only handles INLINE formatting within a single paragraph.
        const formattedText = this.parseMarkdownToYText(textContent);
        element.insert(0, [formattedText]);

        // Safety net: warn if block-level markdown somehow slipped through
        const hasBlockMarkdown = /^#{1,6}\s|^[-*+]\s|^\d+\.\s|^```|^>/m.test(textContent);
        if (hasBlockMarkdown) {
          console.warn('[BaseApplication] ⚠️ Text contains block-level markdown but was inserted as a paragraph — should have been expanded earlier:', {
            preview: textContent.substring(0, 100),
          });
        }

        console.log('[BaseApplication] 📝 Inserted text as Y.XmlText for Notes/Docs:', {
          className,
          textLength: textContent.length,
          hasInlineFormatting: textContent.includes('**') || textContent.includes('*') || textContent.includes('`'),
          hasBlockMarkdown,
          preview: textContent.substring(0, 50),
        });
      }
    }
    
    console.log('[BaseApplication] Created element from className:', {
      className,
      elementType,
      hasPosition: !!attrs.position,
      hasSemantic: !!attrs.semantic,
      hasStyle: !!attrs.style,
      hasTextContent: textContent !== null
    });
    
    return element;
  }
  
  /**
   * Helper: Map Canvas className to element type
   * Used for Pure JSON format in Canvas v2
   */
  private classNameToCanvasType(className: string): string {
    const typeMap: Record<string, string> = {
      'shape-rectangle': 'rectangle',
      'shape-circle': 'circle',
      'shape-ellipse': 'ellipse',
      'shape-triangle': 'triangle',
      'shape-line': 'line',
      'shape-arrow': 'arrow',
      'shape-custom': 'custom',
      'shape-roundedRect': 'roundedRect',
      'shape-pentagon': 'pentagon',
      'shape-hexagon': 'hexagon',
      'shape-star': 'star',
      'shape-diamond': 'diamond',
      'shape-trapezoid': 'trapezoid',
      'shape-parallelogram': 'parallelogram',
      'shape-rightTriangle': 'rightTriangle',
      'shape-pencil': 'pencil',
      'canvas-text': 'text',
      'sticky-note': 'stickyNote',
      'canvas-image': 'image',
      'canvas-button': 'button',
      'canvas-group': 'group',
      'connector': 'line'
    };
    return typeMap[className] || 'rectangle';
  }
  
  /**
   * Helper: Create table row with cells
   */
  private createTableRow(rowData: any): Y.XmlElement {
    const row = new Y.XmlElement('table_row');
    row.setAttribute('id', rowData.attrs?.id || this.generateId());
    
    if (Array.isArray(rowData.content) && rowData.content.length > 0) {
      const cells = rowData.content.map((cellData: any) => {
        const cellType = cellData.type === 'table_header' ? 'table_header' : 'table_cell';
        const cell = new Y.XmlElement(cellType);
        
        cell.setAttribute('id', cellData.attrs?.id || this.generateId());
        if (cellData.attrs?.colspan) cell.setAttribute('colspan', cellData.attrs.colspan);
        if (cellData.attrs?.rowspan) cell.setAttribute('rowspan', cellData.attrs.rowspan);
        if (cellData.attrs?.background) cell.setAttribute('background', cellData.attrs.background);
        
        if (Array.isArray(cellData.content) && cellData.content.length > 0) {
          const cellChildren = cellData.content.map((child: any) => this.contentToYXmlElement(child));
          cell.insert(0, cellChildren);
        } else if (typeof cellData.content === 'string') {
          const paragraph = new Y.XmlElement('paragraph');
          paragraph.setAttribute('id', this.generateId());
          // ✅ FIX: Parse markdown in table cells too!
          const formattedText = this.parseMarkdownToYText(cellData.content);
          paragraph.insert(0, [formattedText]);
          cell.insert(0, [paragraph]);
        } else {
          // Empty cell - add empty paragraph
          const paragraph = new Y.XmlElement('paragraph');
          paragraph.setAttribute('id', this.generateId());
          paragraph.insert(0, [new Y.XmlText('')]);
          cell.insert(0, [paragraph]);
        }
        
        return cell;
      });
      
      row.insert(0, cells);
    } else {
      // Empty row - add empty cell (required by schema: cell+)
      const cell = new Y.XmlElement('table_cell');
      cell.setAttribute('id', this.generateId());
      
      // Cell must have paragraph (block*)
      const paragraph = new Y.XmlElement('paragraph');
      paragraph.setAttribute('id', this.generateId());
      paragraph.insert(0, [new Y.XmlText('')]);
      cell.insert(0, [paragraph]);
      
      row.insert(0, [cell]);
    }
    
    return row;
  }
  
  /**
   * Find nodes by target selector
   */
  private findNodesByTarget(target: any): string[] {
    // ✅ NEW: All nodes (for "replace all content" operations)
    if (target.all === true) {
      // ✅ Use IndexManager for consistency (always fresh!)
      return Array.from(this.indexManager.getAllIds());
    }
    
    // Single node by ID
    if (target.nodeId) {
      return [target.nodeId];
    }
    
    // Multiple nodes by IDs
    if (target.nodeIds) {
      return target.nodeIds;
    }
    
    // All nodes with className
    if (target.className) {
      // ✅ CRITICAL FIX: Use IndexManager instead of deprecated byClassIndex
      // byClassIndex is not synced from IndexManager, causing 0 results!
      return Array.from(this.indexManager.getByClassName(target.className));
    }
    
    // Range of nodes
    if (target.range) {
      // ✅ Use IndexManager for consistency
      const allIds = Array.from(this.indexManager.getAllIds());
      const start = target.range.start || 0;
      const end = target.range.end || allIds.length;
      return allIds.slice(start, end);
    }
    
    // ✅ FALLBACK: If target is empty object, default to "all"
    // This handles cases where AI doesn't know how to target "all content"
    if (Object.keys(target).length === 0) {
      console.warn('[BaseApplication] Empty target selector - defaulting to ALL nodes');
      return Array.from(this.indexManager.getAllIds());
    }
    
    return [];
  }
  
  /**
   * Find node index in Y.XmlFragment by ID
   */
  private findNodeIndexById(nodeId: string): number {
    if (!this.yxml) return -1;
    
    for (let i = 0; i < this.yxml.length; i++) {
      const child = this.yxml.get(i);
      if (child instanceof Y.XmlElement) {
        const id = child.getAttribute('id');
        if (id === nodeId) {
          return i;
        }
      }
    }
    
    return -1;
  }
  
  /**
   * Parse block-level markdown and convert to multiple node objects
   * Handles: # headings, - lists, blank lines → separate paragraphs
   * ✅ FIX: This is needed because AI sends multi-line text with mixed markdown as ONE paragraph!
   */
  private parseBlockMarkdownToNodes(text: string): any[] {
    const lines = text.split('\n');
    const nodes: any[] = [];
    let currentListItems: string[] = [];
    let currentListType: 'bullet' | 'ordered' | null = null;

    const flushList = () => {
      if (currentListItems.length > 0 && currentListType) {
        nodes.push({
          className: currentListType === 'bullet' ? 'note-bullet-list' : 'note-ordered-list',
          attrs: { listItems: [...currentListItems] }
        });
        currentListItems = [];
        currentListType = null;
      }
    };

    let i = 0;
    while (i < lines.length) {
      const line = lines[i];
      const trimmed = line.trim();

      // Empty line → flush any open list
      if (!trimmed) {
        flushList();
        i++;
        continue;
      }

      // Fenced code block: ```[language]
      if (trimmed.startsWith('```')) {
        flushList();
        const language = trimmed.slice(3).trim();
        i++;
        const codeLines: string[] = [];
        // Closing fence is exactly ``` (no language specifier) — prevents matching
        // ``` followed by a language tag that appears as content inside the block.
        while (i < lines.length && !/^```\s*$/.test(lines[i].trim())) {
          codeLines.push(lines[i]);
          i++;
        }
        i++; // consume closing ```
        nodes.push({
          className: 'note-code-block',
          attrs: { language: language || null, text: codeLines.join('\n') }
        });
        continue;
      }

      // Heading: # Title
      const headingMatch = trimmed.match(/^(#{1,6})\s+(.+)$/);
      if (headingMatch) {
        flushList();
        nodes.push({
          className: 'note-heading',
          type: 'heading',
          attrs: { level: Math.min(headingMatch[1].length, 6), text: headingMatch[2] }
        });
        i++;
        continue;
      }

      // Blockquote: > text — collect consecutive blockquote lines
      if (trimmed.startsWith('> ') || trimmed === '>') {
        flushList();
        const bqLines: string[] = [];
        while (i < lines.length && (lines[i].trim().startsWith('> ') || lines[i].trim() === '>')) {
          bqLines.push(lines[i].trim().replace(/^>\s?/, ''));
          i++;
        }
        nodes.push({ className: 'note-blockquote', attrs: { text: bqLines.join('\n') } });
        continue;
      }

      // Bullet list: - item, * item, + item
      const bulletMatch = trimmed.match(/^[-*+]\s+(.+)$/);
      if (bulletMatch) {
        if (currentListType && currentListType !== 'bullet') flushList();
        currentListType = 'bullet';
        currentListItems.push(bulletMatch[1]);
        i++;
        continue;
      }

      // Ordered list: 1. item
      const orderedMatch = trimmed.match(/^\d+\.\s+(.+)$/);
      if (orderedMatch) {
        if (currentListType && currentListType !== 'ordered') flushList();
        currentListType = 'ordered';
        currentListItems.push(orderedMatch[1]);
        i++;
        continue;
      }

      // Regular line → each line is its own paragraph.
      // Joining lines with \n would embed literal \n in Y.XmlText, which HTML collapses to whitespace.
      flushList();
      nodes.push({ className: 'note-paragraph', attrs: { text: trimmed } });
      i++;
    }

    flushList();

    if (nodes.length === 0) {
      return [{ className: 'note-paragraph', attrs: { text: text.trim() || '' } }];
    }

    console.log('[BaseApplication] ✅ Parsed block markdown:', {
      inputLines: lines.length,
      outputNodes: nodes.length,
      nodeTypes: nodes.map(n => n.className || n.type).join(', ')
    });

    return nodes;
  }
  
  /**
   * Parse markdown syntax and convert to formatted Y.XmlText
   * 
   * ✅ FIX: This is the missing piece - entity_write was showing raw `**bold**` 
   * instead of formatted text because it inserted plain Y.XmlText without marks.
   * 
   * Supports:
   * - **bold** → bold text with {bold: true} mark
   * - *italic* → italic text with {italic: true} mark
   * - `code` → code text with {code: true} mark
   * - ***bold+italic*** → text with both marks
   * 
   * Edge cases handled:
   * - Unmatched delimiters (e.g., "**unclosed") → kept as plain text
   * - Empty markers (e.g., "****") → kept as plain text
   * - Math expressions (e.g., "2 * 3") → kept as plain text
   * - Special characters, Unicode, whitespace → preserved correctly
   * 
   * Known limitation (by design):
   * - Nested formatting like "**bold with *italic* inside**" won't work perfectly
   * - Reason: Simple regex can't handle nesting, and it's rare in practice
   * - Alternative: Use separate formats: "**bold** with *italic*"
   * 
   * Performance: O(n) where n is text length, fast even on 10k+ char strings
   */
  private parseMarkdownToYText(text: string): Y.XmlText {
    const yText = new Y.XmlText();

    // Fast path: no markdown patterns present
    if (!text.includes('*') && !text.includes('`') && !text.includes('~') && !text.includes('[')) {
      yText.insert(0, text);
      return yText;
    }

    // Pattern priority (order matters): ***bold+italic***, **bold**, *italic*,
    // `code`, ~~strikethrough~~, [link text](url)
    // Using non-greedy [^\n]+? so matches don't span lines, and * is allowed inside
    // bold/italic spans (e.g. **2 * 2 = 4**).
    const pattern = /(\*\*\*[^\n]+?\*\*\*|\*\*[^\n]+?\*\*|\*[^*\n]+?\*|`[^`\n]+`|~~[^\n]+?~~|\[[^\]\n]+\]\([^)\n]+\))/g;

    let lastIndex = 0;
    let currentPos = 0;
    const segments: Array<{ text: string; marks?: Record<string, any> }> = [];

    let match;
    while ((match = pattern.exec(text)) !== null) {
      if (match.index > lastIndex) {
        segments.push({ text: text.substring(lastIndex, match.index) });
      }

      const matched = match[0];

      if (matched.startsWith('***') && matched.endsWith('***')) {
        segments.push({ text: matched.slice(3, -3), marks: { bold: true, italic: true } });
      } else if (matched.startsWith('**') && matched.endsWith('**')) {
        segments.push({ text: matched.slice(2, -2), marks: { bold: true } });
      } else if (matched.startsWith('*') && matched.endsWith('*')) {
        segments.push({ text: matched.slice(1, -1), marks: { italic: true } });
      } else if (matched.startsWith('`') && matched.endsWith('`')) {
        segments.push({ text: matched.slice(1, -1), marks: { code: true } });
      } else if (matched.startsWith('~~') && matched.endsWith('~~')) {
        segments.push({ text: matched.slice(2, -2), marks: { strikethrough: true } });
      } else if (matched.startsWith('[')) {
        // Link: [text](url)
        const linkMatch = matched.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
        if (linkMatch) {
          segments.push({
            text: linkMatch[1],
            marks: { link: { href: linkMatch[2], target: '_blank' } }
          });
        } else {
          segments.push({ text: matched });
        }
      }

      lastIndex = match.index + matched.length;
    }

    if (lastIndex < text.length) {
      segments.push({ text: text.substring(lastIndex) });
    }

    for (const segment of segments) {
      // Always pass explicit attributes: marks for formatted segments, {} for plain.
      // Without explicit {}, Y.js inherits the current formatting at the insertion
      // position (sticky attributes), causing plain text adjacent to bold/italic spans
      // to incorrectly inherit that formatting.
      yText.insert(currentPos, segment.text, segment.marks || {});
      currentPos += segment.text.length;
    }

    console.log('[BaseApplication] ✅ Parsed inline markdown:', {
      originalText: text.substring(0, 100),
      segmentCount: segments.length,
      formattedSegments: segments.filter(s => s.marks).length
    });

    return yText;
  }
  
  /**
   * Generate unique ID for nodes
   * ✅ PROTECTED: So child classes can use it
   */
  protected generateId(): string {
    return 'node_' + Date.now() + '_' + Math.random().toString(36).substring(2, 15);
  }
  
  /**
   * Generate a unique revision ID for grouping related changes
   * Format: rev_{timestamp}_{random} for sortability and uniqueness
   */
  protected generateRevisionId(): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).slice(2, 9);
    return `rev_${timestamp}_${random}`;
  }
  
  /**
   * Apply change tracking attributes to a Y.XmlElement
   * Used when suggestionMode is enabled to mark AI edits for user review
   * 
   * @param element - Y.XmlElement to mark
   * @param operation - Type of operation (insert, delete, replace)
   * @param revisionId - Unique ID grouping related changes
   */
  protected applyChangeTracking(
    element: Y.XmlElement,
    operation: 'insert' | 'delete' | 'replace',
    revisionId: string
  ): void {
    // Determine change status based on operation
    // 'delete' → 'old' (red strikethrough, will be deleted if accepted)
    // 'insert' or 'replace' → 'new' (green highlight, will be kept if accepted)
    const changeStatus = operation === 'delete' ? 'old' : 'new';
    
    element.setAttribute('change_status', changeStatus);
    element.setAttribute('revision_id', revisionId);
    element.setAttribute('diff_type', `agent_${operation}`);
    
    // ✅ BUG FIX: Don't call getAttribute() before element is in document!
    // Y.js throws "Invalid access" error if you read before insertion
    console.log('[BaseApplication] ✅ Applied change tracking:', {
      changeStatus,
      revisionId: revisionId.slice(-12),
      operation,
      diffType: `agent_${operation}`
      // elementId removed - can't safely read until after insertion
    });
  }
  
  // ========================================================================
  // Additional Command Handlers (Phase 5)
  // ========================================================================
  
  /**
   * Handle entity_format command
   * Apply formatting (bold, italic, color, etc.) to nodes
   */
  async handleEntityFormat(
    target: any,
    format: any
  ): Promise<{ affectedNodes: string[] }> {
    if (!this.ydoc) {
      throw new Error('Y.js not initialized - call load() first');
    }
    
    // ✅ CRITICAL FIX: Rebuild indices BEFORE executing!
    // findNodesByTarget() relies on fresh byIdIndex
    await this.buildCoreIndices();
    console.log('[BaseApplication] ✅ Indices refreshed before format operation');
    
    const affectedNodes: string[] = [];
    
    this.ydoc.transact(() => {
      const nodeIds = this.findNodesByTarget(target);
      
      for (const nodeId of nodeIds) {
        const ref = this.indexManager.getById(nodeId);
        if (!ref) continue;
        
        const element = ref.element;
        
        // Get existing style or create new
        const existingStyle = element.getAttribute('style') || {};
        
        // Merge with new format
        const newStyle = { ...existingStyle, ...format };
        
        // Update element
        element.setAttribute('style', newStyle);
        affectedNodes.push(nodeId);
      }
    });
    
    // Rebuild indices after changes
    await this.buildCoreIndices();
    
    console.log('[BaseApplication] ✅ Applied formatting to', affectedNodes.length, 'nodes');
    
    return { affectedNodes };
  }
  
  /**
   * Handle entity_format_clear command
   * Remove formatting from nodes
   */
  async handleEntityFormatClear(
    target: any,
    formats?: string[]
  ): Promise<{ affectedNodes: string[] }> {
    if (!this.ydoc) {
      throw new Error('Y.js not initialized - call load() first');
    }
    
    // ✅ CRITICAL FIX: Rebuild indices BEFORE executing!
    // findNodesByTarget() relies on fresh byIdIndex
    await this.buildCoreIndices();
    console.log('[BaseApplication] ✅ Indices refreshed before format clear operation');
    
    const affectedNodes: string[] = [];
    
    this.ydoc.transact(() => {
      const nodeIds = this.findNodesByTarget(target);
      
      for (const nodeId of nodeIds) {
        const ref = this.indexManager.getById(nodeId);
        if (!ref) continue;
        
        const element = ref.element;
        const style = element.getAttribute('style') || {};
        
      if (formats && formats.length > 0) {
        // Clear specific formats
        formats.forEach(format => delete (style as any)[format]);
        element.setAttribute('style', JSON.stringify(style));
      } else {
        // Clear all formatting
        element.setAttribute('style', JSON.stringify({}));
      }
        
        affectedNodes.push(nodeId);
      }
    });
    
    await this.buildCoreIndices();
    
    console.log('[BaseApplication] ✅ Cleared formatting from', affectedNodes.length, 'nodes');
    
    return { affectedNodes };
  }
  
  /**
   * Handle entity_add_comment command
   * Add inline comment to a node
   */
  async handleEntityAddComment(
    nodeId: string,
    comment: string,
    metadata: any = {}
  ): Promise<{ commentId: string }> {
    if (!this.ydoc) {
      throw new Error('Y.js not initialized - call load() first');
    }
    
    // Get or create comments map
    const commentsMap = this.ydoc.getMap('comments');
    
    // Generate comment ID
    const commentId = 'comment_' + Date.now() + '_' + Math.random().toString(36).substring(2, 9);
    
    // Get current user (placeholder for now, will be replaced with authService)
    const currentUser = this.getCurrentUser();
    
    // Create comment object
    const commentData = {
      id: commentId,
      nodeId,
      text: comment,
      author: currentUser.id,
      authorName: currentUser.name,
      timestamp: Date.now(),
      resolved: false,
      metadata
    };
    
    // Store in Y.js
    commentsMap.set(commentId, commentData);
    
    console.log('[BaseApplication] ✅ Comment added:', commentId);
    
    return { commentId };
  }
  
  /**
   * Handle entity_export command
   * Export entity to various formats
   */
  async handleEntityExport(
    format: 'markdown' | 'html' | 'pdf' | 'json',
    options: any = {}
  ): Promise<{ content?: string; url?: string; mimeType: string }> {
    switch (format) {
      case 'markdown':
        // Check if app has exportToMarkdown method
        if (typeof (this as any).exportToMarkdown === 'function') {
          const content = await (this as any).exportToMarkdown();
          return {
            content,
            mimeType: 'text/markdown'
          };
        }
        // Fallback: basic markdown conversion
        const mdContent = this.basicMarkdownExport();
        return {
          content: mdContent,
          mimeType: 'text/markdown'
        };
      
      case 'json':
        const jsonData = {
          entityId: this.entityId,
          entityType: this.getConfig().appId,
          exportedAt: new Date().toISOString(),
          nodes: this.serializeAllNodes(),
          metadata: this.getDocumentMetadata(),
          stats: this.getStats()
        };
        return {
          content: JSON.stringify(jsonData, null, 2),
          mimeType: 'application/json'
        };
      
      case 'html':
        const htmlContent = this.basicHtmlExport();
        return {
          content: htmlContent,
          mimeType: 'text/html'
        };
      
      case 'pdf':
        // PDF requires backend service - return URL to generator endpoint
        return {
          url: `/api/export/pdf/${this.entityId}`,
          mimeType: 'application/pdf'
        };
      
      default:
        throw new Error(`Unsupported export format: ${format}`);
    }
  }
  
  /**
   * Basic markdown export (fallback)
   */
  private basicMarkdownExport(): string {
    const lines: string[] = [];
    
    for (const [id, ref] of this.indexManager.getAllEntries()) {
      const element = ref.element;
      const className = element.getAttribute('className');
      const text = this.extractTextFromElement(element);
      
      if (!text) continue;
      
      if (className?.startsWith('heading-')) {
        const level = parseInt(className.split('-')[1], 10);
        lines.push('#'.repeat(level) + ' ' + text);
      } else if (className === 'code-block') {
        lines.push('```');
        lines.push(text);
        lines.push('```');
      } else if (className === 'blockquote') {
        lines.push('> ' + text);
      } else if (className === 'horizontal-rule') {
        lines.push('---');
      } else {
        lines.push(text);
      }
      
      lines.push('');
    }
    
    return lines.join('\n');
  }
  
  /**
   * Basic HTML export
   */
  private basicHtmlExport(): string {
    const lines: string[] = [
      '<!DOCTYPE html>',
      '<html>',
      '<head>',
      '<meta charset="UTF-8">',
      `<title>${this.entityId}</title>`,
      '</head>',
      '<body>'
    ];
    
    for (const [id, ref] of this.indexManager.getAllEntries()) {
      const element = ref.element;
      const className = element.getAttribute('className');
      const text = this.extractTextFromElement(element);
      
      if (!text) continue;
      
      if (className?.startsWith('heading-')) {
        const level = parseInt(className.split('-')[1], 10);
        lines.push(`<h${level}>${this.escapeHtml(text)}</h${level}>`);
      } else if (className === 'code-block') {
        lines.push(`<pre><code>${this.escapeHtml(text)}</code></pre>`);
      } else if (className === 'blockquote') {
        lines.push(`<blockquote>${this.escapeHtml(text)}</blockquote>`);
      } else {
        lines.push(`<p>${this.escapeHtml(text)}</p>`);
      }
    }
    
    lines.push('</body>', '</html>');
    return lines.join('\n');
  }
  
  /**
   * Serialize all nodes for export
   */
  private serializeAllNodes(): any[] {
    const nodes = [];
    
    for (const [id, ref] of this.indexManager.getAllEntries()) {
      nodes.push(this.serializeNode(ref.element));
    }
    
    return nodes;
  }
  
  /**
   * Get document metadata
   */
  private getDocumentMetadata(): any {
    if (!this.ydoc) return {};
    
    const metadata = this.ydoc.getMap('metadata');
    return metadata.toJSON ? metadata.toJSON() : {};
  }
  
  /**
   * Get document statistics
   */
  private getStats(): any {
    return {
      totalNodes: this.indexManager.size(),
      indexStats: this.indexManager.getStats()
    };
  }
  
  /**
   * Escape HTML special characters
   */
  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
  
  /**
   * Get current user (placeholder - will be replaced with authService)
   */
  private getCurrentUser(): { id: string; name: string; email?: string } {
    // This will be replaced with authService integration
    // For now, return placeholder
    return {
      id: 'user-' + Date.now(),
      name: 'Current User'
    };
  }
  
  // ========================================================================
  // Unified Persistence Layer
  // ========================================================================
  
  /**
   * Setup auto-save observer for Y.Doc
   * ✅ FIXED: Now prevents duplicate setup (Bug #3)
   * Call this after loading base64 state into Y.Doc
   * 
   * This watches for local changes and triggers debounced HTTP saves
   * Remote changes (from WebSocket) are ignored to prevent save loops
   */
  protected setupAutoSave(): void {
    console.log('[BaseApplication] 🔧 setupAutoSave() called:', {
      hasYdoc: !!this.ydoc,
      hasEntityId: !!this.entityId,
      autoSaveEnabled: this.autoSaveEnabled,
      alreadySetup: !!(this.debouncedSave || this.ydocObserver)
    });
    
    if (!this.ydoc || !this.entityId) {
      console.warn('[BaseApplication] ⚠️ Cannot setup auto-save - missing ydoc or entityId');
      return;
    }
    
    if (!this.autoSaveEnabled) {
      console.log('[BaseApplication] Auto-save disabled');
      return;
    }
    
    // ✅ FIX (Bug #3): Prevent duplicate setup
    if (this.debouncedSave || this.ydocObserver) {
      console.warn('[BaseApplication] ⚠️ Auto-save already setup, skipping duplicate initialization');
      return;
    }
    
    // Create debounced save function
    this.debouncedSave = debounce(
      async () => {
        await this.saveToBackend();
      },
      this.saveDebounceMs,
      { maxWait: this.saveMaxWaitMs }
    );
    
    console.log('[BaseApplication] ✅ Debounced save function created');
    
    // Create Y.Doc observer
    this.ydocObserver = (update: Uint8Array, origin: any) => {
      // Skip saves for updates we applied ourselves from an offline-save broadcast.
      if (origin === 'offline-save-sync') {
        return;
      }

      // Skip Path 2 content-reload applies — the data was just fetched from REST,
      // so saving it back would be redundant noise.
      if (origin === 'content-reload') {
        return;
      }

      // y-websocket fires doc.on('update') with origin = the inner WebsocketProvider instance,
      // NOT the outer YjsCollaborationProvider wrapper. We must compare against the inner provider.
      const wsProvider = (this.collaborationProvider as any)?.wsProvider;
      const isRemoteWsUpdate = wsProvider ? origin === wsProvider : false;

      if (isRemoteWsUpdate) {
        // Remote WS update — skip auto-save (the server already has this state)
        return;
      }

      // Skip IndexedDB rehydration updates — these replay persisted state on startup
      // and are not user edits. Saving them back would be a no-op at best, or could
      // overwrite newer backend state with old IDB content at worst.
      if (origin?.constructor?.name === 'IndexeddbPersistence') {
        return;
      }

      // All other origins (null, clientID, etc.) are local changes → trigger save
      this.debouncedSave!();
    };
    
    // Attach observer
    this.ydoc.on('update', this.ydocObserver);

    // Flush pending saves when the tab is hidden (covers tab-switch, minimize, and
    // most tab-close scenarios). More reliable than beforeunload because the page
    // is not yet being torn down and async tasks can still complete.
    if (typeof document !== 'undefined') {
      const visibilityHandler = () => {
        if (document.visibilityState === 'hidden' && this.debouncedSave) {
          this.debouncedSave.flush();
        }
      };
      document.addEventListener('visibilitychange', visibilityHandler);
      // Store for cleanup
      (this as any)._visibilityHandler = visibilityHandler;
    }
    
    console.log('[BaseApplication] ✅ Auto-save enabled:', {
      debounceMs: this.saveDebounceMs,
      maxWaitMs: this.saveMaxWaitMs,
      observerAttached: true
    });
  }
  
  /**
   * Save Y.Doc state to backend via HTTP.
   * Pass { force: true } to bypass the WS-synced optimisation (use for
   * explicit save-on-close / navigation so content is always persisted).
   */
  protected async saveToBackend(options?: { force?: boolean }): Promise<void> {
    if (!this.ydoc || !this.entityId) {
      console.log('[BaseApplication] ⚠️ Cannot save - missing ydoc or entityId');
      return;
    }

    // When the WebSocket collaboration provider is connected and synced,
    // the WS server handles persistence via its own debounced save.
    // Skip the redundant client-side REST save — UNLESS this is a forced save
    // (e.g. before navigation or tab close) where we need an immediate guarantee.
    if (!options?.force && this.collaborationProvider && this.collaborationProvider.status === 'synced') {
      console.log(`[BaseApplication] saveToBackend: skipping REST save — WS synced (room=${this.collaborationProvider.getDocumentId?.()?.slice(-8) ?? 'unknown'})`)
      return;
    }
    if (this.collaborationProvider) {
      console.log(`[BaseApplication] saveToBackend: WS present but status=${this.collaborationProvider.status} — proceeding with REST save (force=${!!options?.force})`)
    }
    
    // ✅ FIX (Bug #2): Prevent concurrent saves
    if (this.savePending) {
      console.log('[BaseApplication] 💾 Save already in progress, queueing...');
      this.saveQueued = true;
      return;
    }
    
    this.savePending = true;
    
    try {
      // Reset queue flag before encoding (changes during save will trigger new save)
      this.saveQueued = false;
      
      console.log('[BaseApplication] 💾 Saving to backend...');
      this.emit('saveStart');
      
      // Encode Y.Doc state as binary — V2 format for consistency with WS server persistence.
      const state = Y.encodeStateAsUpdateV2(this.ydoc);

      // An empty Y.Doc V2 update encodes as a small fixed-size header (~6 bytes).
      // Refuse to overwrite existing backend data with a trivially empty doc —
      // this prevents a browser instance that never loaded content from clobbering
      // data that the agent (or another tab) already persisted.
      if (state.length < 20) {
        console.warn('[BaseApplication] ⚠️ Refusing to save trivially empty ydoc state (nothing to persist)');
        this.savePending = false;
        return;
      }

      console.log('[BaseApplication] 💾 Saving to backend...', { entityId: this.entityId, stateSize: state.length });

      // Convert to base64
      const base64 = this.uint8ArrayToBase64(state);

      this.lastSaveTimestamp = Date.now();
      this.lastSaveStateSize = state.length;
      const updatePayload = { skeleton: { base64 } };
      await entityService.update(this.entityId, updatePayload);
      console.log('[BaseApplication] ✅ Saved to backend', { entityId: this.entityId, stateSize: state.length, base64Length: base64.length });

      // Notify any mounted UI instance for this entity so it can merge our state
      // immediately without waiting for a page refresh. Only offline instances broadcast
      // (UI instances sync via WebSocket). Fires at most once per debounce period.
      if (!this.collaborationProvider && this.entityId) {
        window.dispatchEvent(new CustomEvent('entity-offline-saved', {
          detail: { entityId: this.entityId, update: state },
        }));
        console.log('[BaseApplication] 📢 Broadcast offline save:', {
          entityId: this.entityId,
          updateSize: state.length,
        });
      }

      this.emit('saveSuccess');
    } catch (error) {
      console.error('❌❌❌ [SAVE-FAILED] Save to MongoDB FAILED:', error);
      console.error('[BaseApplication] ❌ Error details:', {
        entityId: this.entityId,
        error: error,
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined
      });
      this.emit('saveError', error);
      // Don't throw - save failure shouldn't crash the application
    } finally {
      this.savePending = false;
      
      // ✅ FIX (Bug #2): If changes occurred during save, trigger another
      if (this.saveQueued) {
        console.log('[BaseApplication] 🔄 Re-triggering save for queued changes');
        this.debouncedSave?.();
      }
    }
  }
  
  /**
   * Force an immediate save, bypassing all debounce timers.
   * Always performs an HTTP save regardless of WebSocket sync status.
   * Call this before critical operations (navigation, close, etc.)
   */
  public async forceSave(): Promise<void> {
    // Cancel the pending debounce — we are saving right now, no need for it to
    // fire separately afterward (which would cause a redundant duplicate save).
    if (this.debouncedSave) {
      this.debouncedSave.cancel();
    }
    await this.saveToBackend({ force: true });
  }
  
  /**
   * Load base64 state into Y.Doc
   * Call this after creating Y.Doc but before building indices
   */
  protected loadBase64State(base64: string): void {
    if (!this.ydoc) {
      console.error('[BaseApplication] Cannot load state - no ydoc');
      throw new Error('Y.Doc not initialized');
    }
    
    try {
      const stateBytes = this.base64ToUint8Array(base64);
      // WS server saves V2; try V2 first, fall back to V1 for legacy data.
      try { Y.applyUpdateV2(this.ydoc, stateBytes); } catch { Y.applyUpdate(this.ydoc, stateBytes); }
      
      this.lastSaveStateSize = stateBytes.length;
      
      console.log('[BaseApplication] ✅ Loaded base64 state:', stateBytes.length, 'bytes');
    } catch (error) {
      console.error('[BaseApplication] Failed to load state:', error);
      throw error;
    }
  }
  
  /**
   * ✅ FIX #3: Reload from backend when stale/corrupted data detected
   * Called automatically when WebSocket update validation fails
   */
  private async reloadFromBackend(): Promise<void> {
    if (!this.entityId || !this.ydoc) {
      console.error('[BaseApplication] Cannot reload - missing entityId or ydoc');
      return;
    }
    
    console.log('[BaseApplication] 🔄 Reloading from backend to recover from stale data...');
    
    try {
      // Import entityService
      const { entityService } = await import('../entityService');
      
      // Fetch fresh entity from backend
      const entity = await entityService.get(this.entityId);
      
      if (!entity?.skeleton?.base64) {
        console.error('[BaseApplication] ❌ No base64 state in backend entity');
        return;
      }
      
      console.log('[BaseApplication] 📥 Fetched fresh state from backend:', {
        base64Length: entity.skeleton.base64.length,
        entityUpdatedAt: entity.updated_at
      });
      
      // Clear current Y.Doc state
      this.ydoc.transact(() => {
        // Clear prosemirror fragment
        const yxml = this.ydoc!.getXmlFragment('prosemirror');
        if (yxml && yxml.length > 0) {
          console.log('[BaseApplication] 🧹 Clearing prosemirror fragment:', yxml.length, 'elements');
          yxml.delete(0, yxml.length);
        }
        
        // Clear markdown text (for notes)
        const ytext = this.ydoc!.getText('markdown');
        if (ytext && ytext.length > 0) {
          console.log('[BaseApplication] 🧹 Clearing markdown text:', ytext.length, 'chars');
          ytext.delete(0, ytext.length);
        }
      }, 'reload-from-backend');
      
      // Load fresh state
      this.loadBase64State(entity.skeleton.base64);
      
      console.log('[BaseApplication] ✅ Successfully reloaded from backend');
      
      // Emit event for components to refresh
      this.emit('reloaded');
      
    } catch (error) {
      console.error('[BaseApplication] ❌ Reload from backend failed:', error);
      throw error;
    }
  }
  
  /**
   * Convert Uint8Array to base64
   */
  protected uint8ArrayToBase64(bytes: Uint8Array): string {
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }
  
  /**
   * Convert base64 to Uint8Array
   */
  protected base64ToUint8Array(base64: string): Uint8Array {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }
  
  /**
   * Cleanup persistence resources
   * Called during destroy to remove observers and cancel pending saves
   */
  protected cleanupPersistence(): void {
    console.log('[BaseApplication] Cleaning up persistence resources');
    
    // Remove Y.Doc observer
    if (this.ydoc && this.ydocObserver) {
      this.ydoc.off('update', this.ydocObserver);
      this.ydocObserver = undefined;
    }
    
    // Remove visibilitychange listener registered by setupAutoSave
    if (typeof document !== 'undefined' && (this as any)._visibilityHandler) {
      document.removeEventListener('visibilitychange', (this as any)._visibilityHandler);
      (this as any)._visibilityHandler = undefined;
    }

    // Cancel pending saves
    if (this.debouncedSave) {
      this.debouncedSave.cancel();
      this.debouncedSave = undefined;
    }
    
    console.log('[BaseApplication] ✅ Persistence cleanup complete');
  }
  
  // ========================================================================
  // Helper Methods for Components
  // ========================================================================
  
  /**
   * Get content fragment (for ProseMirror binding)
   */
  getContentFragment(): Y.XmlFragment | null {
    return this.yxml || null;
  }
  
  /**
   * Get Y.js document
   */
  getYDoc(): Y.Doc | null {
    return this.ydoc || null;
  }
  
  /**
   * Get nodes by className (helper for apps)
   */
  protected getNodesByClassName(className: string): any[] {
    const nodeIds = this.indexManager.getByClassName(className);
    return Array.from(nodeIds)
      .map(id => this.indexManager.getById(id))
      .filter(ref => ref !== null)
      .map(ref => ref!.element);
  }
}

